'use client';

import { useGLTF, useAnimations } from '@react-three/drei';
import { useThree, useFrame, useLoader } from '@react-three/fiber';
import { FBXLoader } from 'three/examples/jsm/loaders/FBXLoader.js';
import React, { useEffect, useLayoutEffect, useMemo, useRef, forwardRef, useImperativeHandle, useState, useSyncExternalStore, useCallback } from 'react';
import * as THREE from 'three';
import { applyGraphEnvOverride, envOverrideKey } from './graph-env-override';
import { C3D_UV_RANDOMIZE_UD, graphTextureReadyForGpu, promotePendingCpuProcessTexture } from './material-graph-eval';
import {
  applyPlayerTextureDownscale,
  detachEmbeddedTexturesForPlayer,
  getTextureQualityCacheSuffix,
  getPlayerTextureQualityEpoch,
  optimizeTextureUrl,
  registerPlayerTextureCacheClearHook,
  subscribePlayerTextureQuality,
} from './player-texture-quality';
import {
  registerAssetUrlTextureClearHook,
  disposeTexturesInMapByUrl,
} from './asset-url-invalidation';
import { configureStableTextureSampling, textureHasStableMinFilter, cloneTextureLight, commitTextureUV } from './texture-sampling';
import { applyTransmitBlurDefine } from './transmission-blur-patch';
import { applyTransmissionAttenuation, sanitizeAttenuationDistance } from './transmission-attenuation';
import { extendGltfLoader } from './gltf-loader-setup';
import { cameraLinkedEnvYaw, demoteNestedTransmission, getOrbitTarget } from './camera-env-link';
import { materialTransitionBlend, isMaterialTransitionClockActive } from './material-transition-clock';
import { getSharedGraphMaterial, invalidateSharedGraphMaterial } from './shared-graph-material';
import { ensureGeometryTangents } from './modifier-cpu';

/** Coalesce per-mesh map-ready signals — prod idle SSAA was folding empty frames into history. */
let materialMapsReadyDispatchTimer: ReturnType<typeof setTimeout> | null = null;
function dispatchMaterialMapsReadyDebounced(): void {
  if (typeof document === 'undefined') return;
  if (materialMapsReadyDispatchTimer) clearTimeout(materialMapsReadyDispatchTimer);
  materialMapsReadyDispatchTimer = setTimeout(() => {
    materialMapsReadyDispatchTimer = null;
    document.dispatchEvent(new CustomEvent('scene-material-maps-ready'));
  }, 280);
}

// Suppress non-critical loader noise (does not affect rendering).
if (typeof window !== 'undefined') {
  const _origConsoleError = console.error;
  console.error = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes("GLTFLoader: Couldn't load texture")) return;
    _origConsoleError.apply(console, args);
  };

  // FBX often lists material ShadingModel as "unknown"; FBXLoader falls back to MeshPhongMaterial.
  const _origConsoleWarn = console.warn;
  console.warn = (...args: unknown[]) => {
    if (typeof args[0] === 'string' && args[0].includes('THREE.FBXLoader: unknown material type')) return;
    _origConsoleWarn.apply(console, args);
  };
}

// ---------------------------------------------------------------------------
// Patch Three.js ShaderChunk so bump map stacks on top of normal map
// instead of being mutually exclusive (#elif → separate #ifdef).
// ---------------------------------------------------------------------------
(() => {
  const chunk = THREE.ShaderChunk as Record<string, string>;
  const key = 'normal_fragment_maps';
  if (!chunk[key]) return;
  const exclusive = '#elif defined( USE_BUMPMAP )';
  if (chunk[key].includes(exclusive)) {
    chunk[key] = chunk[key].replace(exclusive, '#endif\n#ifdef USE_BUMPMAP');
  }
})();

// ---------------------------------------------------------------------------
// Rounded edges via bump-from-edge-proximity.
// At hard edges, vertices are split so each face has its own normal. We
// pre-compute "smoothed" normals by averaging all normals at the same
// position and pass them as a vertex attribute. In the fragment shader,
// the dot-product deviation between the original and smooth normal gives
// a height field that ramps up near edges. We take the screen-space
// gradient of this height field (dFdx/dFdy — the same technique as
// https://madebyevan.com/shaders/grid/) and use it to perturb the
// surface normal, creating a bump-map-style chamfer highlight at edges.
// ---------------------------------------------------------------------------
const ROUNDED_EDGE_FRAG = /* glsl */ `#include <normal_fragment_maps>
{
  vec3 reSmooth = normalize(vSmoothNormal);
  float reH = 1.0 - abs(dot(normal, reSmooth));
  float reHdx = dFdx(reH);
  float reHdy = dFdy(reH);
  vec3 reSdx = dFdx(vViewPosition);
  vec3 reSdy = dFdy(vViewPosition);
  vec3 reR1 = cross(reSdy, normal);
  vec3 reR2 = cross(normal, reSdx);
  float reDet = dot(reSdx, reR1);
  vec3 reSurfGrad = sign(reDet) * (reHdx * reR1 + reHdy * reR2);
  normal = normalize(abs(reDet) * normal - u_roundedEdgeRadius * reSurfGrad);
  // DEBUG: tint edge regions green so we can confirm the shader runs
  diffuseColor.rgb = mix(diffuseColor.rgb, vec3(0.0, 1.0, 0.0), reH * 5.0);
}`;

// ---------------------------------------------------------------------------
function ensureSmoothedNormals(geometry: THREE.BufferGeometry): boolean {
  if (geometry.getAttribute('smoothNormal')) return true;
  const posAttr = geometry.getAttribute('position');
  const normAttr = geometry.getAttribute('normal');
  if (!posAttr || !normAttr) return false;

  const count = posAttr.count;
  const eps = 1e-4;
  const groups = new Map<string, number[]>();

  for (let i = 0; i < count; i++) {
    const kx = Math.round(posAttr.getX(i) / eps);
    const ky = Math.round(posAttr.getY(i) / eps);
    const kz = Math.round(posAttr.getZ(i) / eps);
    const key = `${kx},${ky},${kz}`;
    let g = groups.get(key);
    if (!g) { g = []; groups.set(key, g); }
    g.push(i);
  }

  const smoothed = new Float32Array(count * 3);

  for (const indices of groups.values()) {
    let ax = 0, ay = 0, az = 0;
    for (const i of indices) {
      ax += normAttr.getX(i);
      ay += normAttr.getY(i);
      az += normAttr.getZ(i);
    }
    const len = Math.sqrt(ax * ax + ay * ay + az * az) || 1;
    ax /= len; ay /= len; az /= len;
    for (const i of indices) {
      smoothed[i * 3] = ax;
      smoothed[i * 3 + 1] = ay;
      smoothed[i * 3 + 2] = az;
    }
  }

  geometry.setAttribute('smoothNormal', new THREE.BufferAttribute(smoothed, 3));
  return true;
}

// ---------------------------------------------------------------------------
// Legacy material overrides (simple, non-per-mesh)
// ---------------------------------------------------------------------------
export interface MaterialOverrides {
  color?: string;
  roughness?: number;
  metalness?: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  emissive?: string;
  emissiveIntensity?: number;
  transmission?: number;
  ior?: number;
}

// ---------------------------------------------------------------------------
// Per-mesh PBR overrides (advanced)
// ---------------------------------------------------------------------------
export interface PBRMeshOverrideData {
  color?: string;
  roughness?: number;
  metalness?: number;
  emissive?: string;
  emissiveIntensity?: number;
  clearcoat?: number;
  clearcoatRoughness?: number;
  transmission?: number;
  /** Transmission blur strength (custom shader path). */
  transmitBlur?: number;
  opacity?: number;
  ior?: number;
  sheenColor?: string;
  sheenRoughness?: number;
  anisotropy?: number;
  anisotropyRotation?: number;
  thickness?: number;
  /** Beer–Lambert absorption tint for transmission (colored liquids). */
  attenuationColor?: string;
  /** Average distance before transmitted light is absorbed; omit for auto from base color. */
  attenuationDistance?: number;
  specularIntensity?: number;
  specularColor?: string;
  envMapIntensity?: number;

  map?: string | null;
  normalMap?: string | null;
  bumpMap?: string | null;
  roughnessMap?: string | null;
  metalnessMap?: string | null;
  emissiveMap?: string | null;
  aoMap?: string | null;
  displacementMap?: string | null;
  alphaMap?: string | null;
  anisotropyMap?: string | null;

  mapRepeat?: [number, number];
  mapOffset?: [number, number];
  mapRotation?: number;
  /**
   * When true, this mesh skips renderer ACES/AgX (keeps albedo saturation).
   * No effect on a full-frame post ToneMapping pass.
   */
  skipToneMap?: boolean;
}

export type PerMeshOverrides = Record<string, Partial<PBRMeshOverrideData>>;

export interface AnimationControls {
  playing?: boolean;
  speed?: number;
  clipName?: string;
}

export interface GlbModelHandle {
  getMeshNames: () => string[];
  getGroup: () => THREE.Group | null;
}

export type DisplayMode = 'default' | 'matteWhite' | 'glossyGray' | 'normals' | 'uvChecker' | 'graphOverride';

export interface GlbModelProps {
  url: string;
  position?: [number, number, number];
  rotation?: [number, number, number];
  scale?: number | [number, number, number];
  materialOverrides?: MaterialOverrides;
  perMeshOverrides?: PerMeshOverrides;
  meshVisibility?: Record<string, boolean>;
  highlightMeshName?: string | null;
  visible?: boolean;
  castShadow?: boolean;
  receiveShadow?: boolean;
  onClick?: (e?: any) => void;
  onPointerOver?: (e?: any) => void;
  onPointerOut?: (e?: any) => void;
  animation?: AnimationControls;
  onAnimationsLoaded?: (clipNames: string[]) => void;
  onLoaded?: () => void;
  /** Fires once materials/textures for this model are ready for first paint (see holdUntilReady). */
  onContentReady?: () => void;
  /**
   * When true, keep the model invisible until graph maps / deferred textures are ready.
   * Used by the player first-reveal gate so the fade-in never shows a half-built product.
   */
  holdUntilReady?: boolean;
  /** When set, skip keep-previous-finish-until-maps-ready (mobile: wood was stuck on prior look). */
  preferProgressiveMaterials?: boolean;
  /**
   * Player: false until rule `derived` exists and material graphs have been evaluated.
   * Prevents marking content-ready on raw GLB defaults, then “building up from white”
   * when overrides arrive a frame later. Default true (editor / no gate).
   */
  materialsGateReady?: boolean;
  onMeshNamesReady?: (names: string[]) => void;
  wireframe?: boolean;
  displayMode?: DisplayMode;
  materialGraphOverrides?: Record<string, Record<string, any>> | null;
  /** Seconds for scalar PBR crossfade when texture set is unchanged. */
  transitionDuration?: number;
  transitionStartedAt?: number;
  /**
   * Seconds for rule show/hide opacity fades. Prefer `0` (snap) so material swaps do not
   * leave outgoing/incoming/third objects overlapping on screen.
   * When omitted, falls back to `transitionDuration` for backwards compatibility.
   */
  opacityTransitionDuration?: number;
  opacityOverride?: number;
  onFadeOutComplete?: () => void;
  excludeFromSSAO?: boolean;
  /** Omit this object from the glass transmission / refraction buffer (surface-mounted trim). */
  excludeFromRefraction?: boolean;
  depthOffset?: number;
  stayOnTop?: boolean;
}

/**
 * `ModifiedMesh` hides source meshes and draws `InstancedMesh` copies. Those instances must use the **same**
 * material reference as the source so graph-driven `meshTransition` / `lerpSnap` update what the user sees.
 * Without this, React effects can assign a new material to the source one frame before `ModifiedMesh`'s
 * `useFrame` sync — cap (non-instanced) vs arrayed parts look temporally offset on multi-layer materials (e.g. car paint).
 */
function syncInstancedMeshMaterialFromSource(sourceMesh: THREE.Mesh) {
  let p: THREE.Object3D | null = sourceMesh.parent;
  while (p) {
    const getter = (p.userData as { getC3dInstancedMeshes?: () => THREE.InstancedMesh[] }).getC3dInstancedMeshes;
    if (typeof getter === 'function') {
      const instanced = getter();
      if (!instanced.length) return;
      const sources: THREE.Mesh[] = [];
      p.traverse((c) => {
        if (c instanceof THREE.Mesh) sources.push(c);
      });
      const idx = sources.indexOf(sourceMesh);
      if (idx < 0 || idx >= instanced.length) return;
      const im = instanced[idx];
      const ud = im.userData as { __c3dDisposeMat?: boolean };
      if (ud.__c3dDisposeMat) return;
      const m = sourceMesh.material;
      if (m && !Array.isArray(m)) im.material = m as THREE.Material;
      return;
    }
    p = p.parent;
  }
}

/** Wildcard graph (`*`) applies to every mesh; unnamed GLB meshes must still skip global `materialOverrides`. */
function meshUsesMaterialGraph(
  materialGraphOverrides: Record<string, Record<string, any>> | null | undefined,
  meshName: string | undefined,
): boolean {
  if (!materialGraphOverrides) return false;
  if (materialGraphOverrides['*']) return true;
  return !!meshName && !!materialGraphOverrides[meshName];
}

const textureLoader = new THREE.TextureLoader();
const textureCache = new Map<string, THREE.Texture>();

registerPlayerTextureCacheClearHook(() => {
  for (const tex of textureCache.values()) {
    tex.dispose();
  }
  textureCache.clear();
});

registerAssetUrlTextureClearHook((oldUrl) => {
  disposeTexturesInMapByUrl(textureCache, oldUrl);
});

let _uvCheckerTexture: THREE.CanvasTexture | null = null;
function getUvCheckerTexture(): THREE.CanvasTexture {
  if (_uvCheckerTexture) return _uvCheckerTexture;
  const size = 512;
  const cells = 16;
  const cellSize = size / cells;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const colors = ['#e74c3c', '#3498db', '#2ecc71', '#f39c12', '#9b59b6', '#1abc9c'];
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      const isEven = (x + y) % 2 === 0;
      ctx.fillStyle = isEven ? colors[(x + y * cells) % colors.length] : '#222';
      ctx.fillRect(x * cellSize, y * cellSize, cellSize, cellSize);
    }
  }
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${cellSize * 0.35}px monospace`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  for (let y = 0; y < cells; y++) {
    for (let x = 0; x < cells; x++) {
      if ((x + y) % 2 === 0) {
        ctx.fillText(`${x},${y}`, x * cellSize + cellSize / 2, y * cellSize + cellSize / 2);
      }
    }
  }
  const tex = new THREE.CanvasTexture(canvas);
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  tex.flipY = false;
  tex.colorSpace = THREE.SRGBColorSpace;
  _uvCheckerTexture = tex;
  return tex;
}

const LINEAR_TEX_SLOTS = new Set([
  'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
  'aoMap', 'displacementMap', 'alphaMap', 'anisotropyMap',
  'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
]);

const PBR_OVERRIDE_TEX_KEYS = [
  'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
  'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
  'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
] as const;

/** Prime GlbModel texture cache for library / rule PBR blobs that use string URLs. */
export function prefetchPbrOverrideTextures(data: Record<string, unknown> | null | undefined): void {
  if (!data) return;
  for (const key of PBR_OVERRIDE_TEX_KEYS) {
    const v = data[key];
    if (typeof v !== 'string' || v.length === 0) continue;
    try {
      loadTexture(v, LINEAR_TEX_SLOTS.has(key));
    } catch {
      /* ignore */
    }
  }
}

function loadTexture(url: string, linear = false): THREE.Texture {
  const qSuffix = getTextureQualityCacheSuffix();
  const cacheKey = `${url}__${linear ? 'L' : 'S'}__${qSuffix}`;
  if (textureCache.has(cacheKey)) return textureCache.get(cacheKey)!;
  const fetchUrl = optimizeTextureUrl(url);
  const tex = textureLoader.load(
    fetchUrl,
    (loaded) => {
      applyPlayerTextureDownscale(loaded);
      configureStableTextureSampling(loaded);
    },
    undefined,
    () => {
      console.warn('[GlbModel] Failed to load texture:', url);
      textureCache.delete(cacheKey);
    },
  );
  tex.colorSpace = linear ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
  tex.flipY = false;
  tex.wrapS = THREE.RepeatWrapping;
  tex.wrapT = THREE.RepeatWrapping;
  textureCache.set(cacheKey, tex);
  return tex;
}

function ensureTangents(mesh: THREE.Mesh, options?: { force?: boolean }): void {
  if (!mesh.geometry) return;
  // Prefer UV-derived tangents (MikkT). Keep authored / already-valid tangents unless
  // `force` — Shell/UV CPU mods already rebuild via rebuildTangentsAfterCpuModifiers.
  ensureGeometryTangents(mesh.geometry, options);
}

/**
 * Anisotropy (and often normal mapping) on imported GLBs: exporters frequently include a
 * TANGENT accessor that is wrong / zero / UV-mismatched for Three's anisotropic GGX.
 * Primitives (Sphere) have no authored tangents, so MikkT runs and aniso looks correct.
 * Always rebuild UV tangents when anisotropy is active.
 */
function ensureAnisotropyTangents(mesh: THREE.Mesh): void {
  ensureTangents(mesh, { force: true });
}

/**
 * Three.js multiplies anisotropyMap by anisotropyVector (from scalar anisotropy + rotation).
 * If anisotropy ≤ 0, the sampled map contribution is zeroed (lights_physical_fragment).
 * Never clear the map when tangents are missing — ensureTangents always falls back when
 * normals exist; killing aniso here made Array Radial views look isotropic while the
 * material sphere thumbnail (own geometry) still showed the stretch.
 */
function syncAnisotropyMapStrength(mat: THREE.MeshPhysicalMaterial, mesh: THREE.Mesh): void {
  const needsAniso = (mat.anisotropy ?? 0) > 0 || !!mat.anisotropyMap;
  if (!needsAniso) return;
  ensureAnisotropyTangents(mesh);
  if (mat.anisotropyMap && mat.anisotropy <= 0) mat.anisotropy = 1;
}

/** Rebuild UV tangents when normal / clearcoat-normal / anisotropy maps are present
 *  — or when the graph is about to assign them (deferred load). Without this, Metal
 *  stays flat until a Yellow↔Silver swipe re-runs apply after the normal is already on-mat. */
function ensureTangentsForMappedMaterial(
  mesh: THREE.Mesh,
  mat: THREE.MeshPhysicalMaterial | THREE.MeshStandardMaterial,
  overrides?: Record<string, any>,
): void {
  const phys = mat as THREE.MeshPhysicalMaterial;
  const ovHas = (key: string) => {
    if (!overrides) return false;
    const v = overrides[key];
    return v instanceof THREE.Texture || (typeof v === 'string' && !!v);
  };
  const anisoOn =
    ovHas('anisotropyMap') ||
    (typeof overrides?.anisotropy === 'number' && overrides.anisotropy > 0) ||
    (phys.anisotropyMap && (phys.anisotropy ?? 0) > 0) ||
    (phys.anisotropy ?? 0) > 0;
  if (anisoOn) {
    ensureAnisotropyTangents(mesh);
    return;
  }
  if (
    mat.normalMap ||
    phys.clearcoatNormalMap ||
    ovHas('normalMap') ||
    ovHas('clearcoatNormalMap')
  ) {
    ensureTangents(mesh);
  }
}

/** Avoid THREE.WebGLRenderer "Texture marked for update but no image data" when needsUpdate runs before the GPU can upload. */
function textureHasUploadableData(tex: THREE.Texture): boolean {
  const ud = tex.userData as Record<string, unknown> | undefined;
  // UV-transform clones made before the network image decoded have no image of their
  // own — adopt it from the source texture once the loader fills it in. Without this,
  // first-load Metal normal/roughness clones stay pending until the next graph re-eval.
  if (!tex.image && ud?.__c3dCloneSource) {
    const src = ud.__c3dCloneSource as THREE.Texture;
    if (src.image) {
      tex.image = src.image;
      delete ud.__c3dCloneSource;
      tex.needsUpdate = true;
    }
  }
  return graphTextureReadyForGpu(tex);
}

/**
 * Queue a map that is not GPU-ready yet.
 * Exposure/HSL CPU bakes used to show `__c3dBakeStandIn` (raw pre-process image) first —
 * Metal Mesh looked bright, then dark once Exposure (-0.3) finished. We sync-finish the
 * bake when applying to a live mesh (cheap: one texture, not every library graph on load),
 * and never flash the unprocessed stand-in.
 */
function deferPendingTexture(
  pendingByMat: Map<THREE.Material, Record<string, THREE.Texture>>,
  mat: THREE.MeshPhysicalMaterial,
  slot: string,
  tex: THREE.Texture,
): void {
  // Finish Exposure / HSL / etc. now if the source image is already decoded.
  promotePendingCpuProcessTexture(tex);

  if (textureHasUploadableData(tex)) {
    if (!textureHasStableMinFilter(tex)) configureStableTextureSampling(tex);
    (mat as any)[slot] = tex;
    // Drop any stale pending entry for this slot.
    const bag = pendingByMat.get(mat);
    if (bag) {
      delete bag[slot];
      if (Object.keys(bag).length === 0) pendingByMat.delete(mat);
    }
    return;
  }

  let bag = pendingByMat.get(mat);
  if (!bag) {
    bag = {};
    pendingByMat.set(mat, bag);
  }
  bag[slot] = tex;

  // Do NOT assign raw `__c3dBakeStandIn` (pre-Exposure / pre-HSL) — that is the bright→dark
  // two-step. Keep the previous map only when it is the same bake in flight; otherwise a
  // 1×1 white placeholder so shader defines stay on (Carpaint tint / Wood gloss).
  const cur = (mat as any)[slot] as THREE.Texture | null | undefined;
  if (cur && isInFlightBakeOf(tex, cur) && !isBakePlaceholder(cur)) return;
  holdPendingMapSlot(mat, slot);
}

/** mapRepeat/mapOffset may be stored as [x,y], {x,y}, or bad JSON — avoid passing garbage into Vector2.set (reads .x internally). */
function toVec2Pair(v: unknown): [number, number] | null {
  if (v == null) return null;
  if (Array.isArray(v) && v.length >= 2) {
    const x = Number(v[0]);
    const y = Number(v[1]);
    if (Number.isFinite(x) && Number.isFinite(y)) return [x, y];
    return null;
  }
  if (typeof v === 'object') {
    const o = v as { x?: unknown; y?: unknown };
    const x = Number(o.x);
    const y = Number(o.y);
    if (Number.isFinite(x) && Number.isFinite(y)) return [x, y];
  }
  return null;
}

const CSS_COLOR_RE = /^#([0-9a-f]{3,8})$|^rgb/i;
const NAMED_CSS_COLORS = new Set([
  'black','silver','gray','white','maroon','red','purple','fuchsia','green',
  'lime','olive','yellow','navy','blue','teal','aqua','orange','aliceblue',
  'antiquewhite','aquamarine','azure','beige','bisque','blanchedalmond','blueviolet',
  'brown','burlywood','cadetblue','chartreuse','chocolate','coral','cornflowerblue',
  'cornsilk','crimson','cyan','darkblue','darkcyan','darkgoldenrod','darkgray',
  'darkgreen','darkgrey','darkkhaki','darkmagenta','darkolivegreen','darkorange',
  'darkorchid','darkred','darksalmon','darkseagreen','darkslateblue','darkslategray',
  'darkslategrey','darkturquoise','darkviolet','deeppink','deepskyblue','dimgray',
  'dimgrey','dodgerblue','firebrick','floralwhite','forestgreen','gainsboro',
  'ghostwhite','gold','goldenrod','greenyellow','grey','honeydew','hotpink',
  'indianred','indigo','ivory','khaki','lavender','lavenderblush','lawngreen',
  'lemonchiffon','lightblue','lightcoral','lightcyan','lightgoldenrodyellow',
  'lightgray','lightgreen','lightgrey','lightpink','lightsalmon','lightseagreen',
  'lightskyblue','lightslategray','lightslategrey','lightsteelblue','lightyellow',
  'limegreen','linen','magenta','mediumaquamarine','mediumblue','mediumorchid',
  'mediumpurple','mediumseagreen','mediumslateblue','mediumspringgreen',
  'mediumturquoise','mediumvioletred','midnightblue','mintcream','mistyrose',
  'moccasin','navajowhite','oldlace','olivedrab','orangered','orchid',
  'palegoldenrod','palegreen','paleturquoise','palevioletred','papayawhip',
  'peachpuff','peru','pink','plum','powderblue','rosybrown','royalblue',
  'saddlebrown','salmon','sandybrown','seagreen','seashell','sienna','skyblue',
  'slateblue','slategray','slategrey','snow','springgreen','steelblue','tan',
  'thistle','tomato','turquoise','violet','wheat','whitesmoke','yellowgreen',
]);

function isValidColor(v: string): boolean {
  return CSS_COLOR_RE.test(v) || NAMED_CSS_COLORS.has(v.toLowerCase());
}

function safeColorSet(target: THREE.Color, value: string | undefined): void {
  if (!value || typeof value !== 'string') return;
  if (!isValidColor(value)) return;
  try {
    target.set(value);
  } catch {
    /* invalid color string */
  }
}

function isStandardLikeMaterial(m: THREE.Material): m is THREE.MeshStandardMaterial | THREE.MeshPhysicalMaterial {
  return m instanceof THREE.MeshStandardMaterial || m instanceof THREE.MeshPhysicalMaterial;
}

function alphaMapReady(mat: THREE.Material): boolean {
  const am = (mat as any).alphaMap as THREE.Texture | null | undefined;
  return am != null && textureHasUploadableData(am);
}

/**
 * Letter/logo masks are cutouts (A2C + depth write) at opacity 1.
 * Opacity < 1 must actually blend — A2C turns interior pixels into coverage,
 * so stave grain never shows through a "slightly transparent" engraving.
 */
function applyAlphaMaskFlags(mat: THREE.Material, opacity: number = (mat as THREE.MeshStandardMaterial).opacity ?? 1) {
  const std = mat as THREE.MeshStandardMaterial;
  const hasAlpha = alphaMapReady(mat) || !!(std as any).alphaMap;
  const blend = opacity < 0.999;
  if (!hasAlpha && !blend) return;
  std.transparent = true;
  std.depthWrite = !blend;
  if ('alphaToCoverage' in std) {
    std.alphaToCoverage = hasAlpha && !blend;
  }
}

const GRAPH_TEX_SLOT_KEYS = [
  'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
  'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
  'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
  // GLB/Physical seeds often leave these on — they multiply specular and make Wood
  // look lacquered in the player while the editor graph never wired them.
  'specularIntensityMap', 'specularColorMap',
  'sheenColorMap', 'sheenRoughnessMap',
  'transmissionMap', 'thicknessMap', 'iridescenceMap',
] as const;

/**
 * 1×1 white stand-in while a CPU bake (Desaturate / Brightness / HSL) is in flight.
 * Keeps USE_MAP / USE_ROUGHNESSMAP compiled so Carpaint doesn't flash a darker tint
 * (color × missing/raw albedo) and Wood doesn't flash glossy (roughness map popping in).
 */
const pendingSlotPlaceholders = new Map<string, THREE.DataTexture>();

function isBakePlaceholder(tex: THREE.Texture | null | undefined): boolean {
  return !!(tex?.userData as Record<string, unknown> | undefined)?.__c3dBakePlaceholder;
}

function placeholderForTexSlot(slot: string): THREE.DataTexture {
  const linear = LINEAR_TEX_SLOTS.has(slot);
  const cacheKey = `${slot}:${linear ? 'L' : 'S'}`;
  const cached = pendingSlotPlaceholders.get(cacheKey);
  if (cached) return cached;
  const data = new Uint8Array([255, 255, 255, 255]);
  const tex = new THREE.DataTexture(data, 1, 1);
  tex.needsUpdate = true;
  tex.colorSpace = linear ? THREE.LinearSRGBColorSpace : THREE.SRGBColorSpace;
  tex.wrapS = THREE.ClampToEdgeWrapping;
  tex.wrapT = THREE.ClampToEdgeWrapping;
  tex.magFilter = THREE.NearestFilter;
  tex.minFilter = THREE.NearestFilter;
  tex.generateMipmaps = false;
  (tex.userData as Record<string, unknown>).__c3dBakePlaceholder = true;
  pendingSlotPlaceholders.set(cacheKey, tex);
  return tex;
}

/** Bind a look-preserving placeholder instead of null / raw source while a bake is pending. */
function holdPendingMapSlot(mat: THREE.MeshPhysicalMaterial, key: string): boolean {
  const cur = (mat as any)[key] as THREE.Texture | null | undefined;
  if (isBakePlaceholder(cur)) return false;
  // Keep a finished map (Carpaint / Wood) instead of a 1×1 white stand-in while Straw loads.
  if (cur && textureHasUploadableData(cur) && !isPendingCpuBake(cur)) return false;
  (mat as any)[key] = placeholderForTexSlot(key);
  return true;
}

/**
 * Queue incoming graph maps that are not GPU-ready yet. Does not bind placeholders —
 * caller keeps the live material on screen until these decode / finish CPU bake.
 * Returns true when at least one slot is still pending.
 */
function queueUnreadyGraphTextures(
  pendingByMat: Map<THREE.Material, Record<string, THREE.Texture>>,
  mat: THREE.MeshPhysicalMaterial,
  overrides: Record<string, any>,
): boolean {
  pendingByMat.delete(mat);
  const consider = (slot: string, tex: unknown) => {
    if (!(tex instanceof THREE.Texture)) return;
    promotePendingCpuProcessTexture(tex);
    if (textureHasUploadableData(tex)) return;
    let bag = pendingByMat.get(mat);
    if (!bag) {
      bag = {};
      pendingByMat.set(mat, bag);
    }
    bag[slot] = tex;
  };
  if (overrides.color instanceof THREE.Texture) consider('map', overrides.color);
  for (const key of GRAPH_TEX_SLOT_KEYS) consider(key, overrides[key]);
  const layers = overrides._materialLayers as Array<Record<string, unknown>> | undefined;
  if (Array.isArray(layers)) {
    layers.forEach((layer, i) => {
      if (!layer) return;
      for (const k of ['mask', 'map', 'normalMap', 'bumpMap', 'roughnessMap'] as const) {
        consider(`layer${i}:${k}`, layer[k]);
      }
    });
  }
  const bag = pendingByMat.get(mat);
  return !!bag && Object.keys(bag).length > 0;
}

function isPendingCpuBake(tex: unknown): tex is THREE.Texture {
  return (
    tex instanceof THREE.Texture &&
    !!(tex.userData as Record<string, unknown> | undefined)?.__c3dBakePending
  );
}

/** Stable feature key — when this matches and maps are unchanged, skip shader rebuild. */
function graphFeatureCacheKey(overrides: Record<string, any>): string {
  const layers = overrides._materialLayers as unknown[] | undefined;
  const coat = typeof overrides.clearcoat === 'number' ? overrides.clearcoat : 0;
  const coatR = typeof overrides.clearcoatRoughness === 'number' ? overrides.clearcoatRoughness : 0;
  const coatMaps =
    (overrides.clearcoatMap instanceof THREE.Texture ? '1' : '0') +
    (overrides.clearcoatRoughnessMap instanceof THREE.Texture ? '1' : '0') +
    (overrides.clearcoatNormalMap instanceof THREE.Texture ? '1' : '0');
  const anisoOn =
    (typeof overrides.anisotropy === 'number' && overrides.anisotropy > 0) ||
    overrides.anisotropyMap instanceof THREE.Texture;
  const share =
    typeof overrides.__c3dShareKey === 'string' ? overrides.__c3dShareKey : '';
  return [
    // Different library materials must never soft-reuse each other's compiled path.
    share ? `sk:${share}` : '',
    overrides._falloff ? 'f' : '',
    overrides._roundedEdge ? 'r' : '',
    overrides._invertAlpha ? 'a' : '',
    layers ? `l${Math.min(layers.length, 3)}` : '',
    (() => {
      const ek = envOverrideKey(overrides._envOverride);
      return ek ? `env:${ek}` : '';
    })(),
    (overrides.transmission ?? 0) > 0 ? 't' : '',
    // Full coat signature — on/off alone missed coatRoughness / map bleed between materials.
    `c:${coat.toFixed(3)}:${coatR.toFixed(3)}:${coatMaps}`,
    // Carbon Fiber → Wood used to soft-swap and keep anisotropy / sheen defines.
    anisoOn ? 'aniso' : '',
  ].join('|');
}

/**
 * Single source of truth for clearcoat / sheen / transmission on reused graph mats.
 * Always writes scalars — never `if (overrides.clearcoat != null)` — so a material that
 * omits coat cannot inherit Carpaint (or any prior) lacquer.
 *
 * Coat *maps*: only clear when the graph does not drive that slot. Unconditionally
 * nulling them on the soft color path made Carpaint's texSig flip (clearcoatMap present
 * → absent) and hard-snapped gemstone tints; Leather has no coat maps so it still lerped.
 * Soft/full texture paths re-assign when overrides provide maps.
 */
function applyCoatStackFromOverrides(
  mat: THREE.MeshPhysicalMaterial,
  overrides: Record<string, any>,
): void {
  mat.clearcoat = typeof overrides.clearcoat === 'number' ? overrides.clearcoat : 0;
  mat.clearcoatRoughness =
    typeof overrides.clearcoatRoughness === 'number' ? overrides.clearcoatRoughness : 0;
  if (!(overrides.clearcoatMap instanceof THREE.Texture)) mat.clearcoatMap = null;
  if (!(overrides.clearcoatRoughnessMap instanceof THREE.Texture)) mat.clearcoatRoughnessMap = null;
  if (!(overrides.clearcoatNormalMap instanceof THREE.Texture)) mat.clearcoatNormalMap = null;
  mat.transmission = typeof overrides.transmission === 'number' ? overrides.transmission : 0;

  if (typeof overrides.sheenColor === 'string' && overrides.sheenColor && overrides.sheenColor !== '#000000') {
    safeColorSet(mat.sheenColor, overrides.sheenColor);
    if (mat.sheen === 0) mat.sheen = 1;
  } else {
    mat.sheen = 0;
    safeColorSet(mat.sheenColor, '#000000');
  }
  if (typeof overrides.sheenRoughness === 'number') mat.sheenRoughness = overrides.sheenRoughness;
  else mat.sheenRoughness = 0;
}

/** Zero coat stack after seeding from a live/previous mat (share-key migrate). */
function resetCoatStack(mat: THREE.MeshPhysicalMaterial): void {
  mat.clearcoat = 0;
  mat.clearcoatRoughness = 0;
  mat.clearcoatMap = null;
  mat.clearcoatRoughnessMap = null;
  mat.clearcoatNormalMap = null;
  mat.sheen = 0;
  safeColorSet(mat.sheenColor, '#000000');
  mat.sheenRoughness = 0;
}

function graphTexturesUnchanged(
  mat: THREE.MeshPhysicalMaterial,
  overrides: Record<string, any>,
): boolean {
  for (const key of GRAPH_TEX_SLOT_KEYS) {
    const val = overrides[key];
    const cur = (mat as any)[key] as THREE.Texture | null | undefined;
    // Raw-diffuse stand-in must never count as "unchanged" vs a finished HSL/CPU bake.
    if (cur && (cur.userData as Record<string, unknown> | undefined)?.__c3dIsBakeStandInDisplay) {
      return false;
    }
    if (isBakePlaceholder(cur)) return false;
    if (val instanceof THREE.Texture) {
      // Keep re-entering the soft apply path while a CPU bake is still pending so
      // Desaturate→Brightness maps are deferred/promoted instead of left as null forever.
      if ((val.userData as Record<string, unknown> | undefined)?.__c3dBakePending) {
        return false;
      }
      if (!texturesVisuallySame(cur, val)) return false;
    } else if (typeof val === 'string' && val) {
      // URL-based overrides always go through the full path (may load new Texture instances).
      return false;
    } else if (val === null) {
      // Explicit clear (disabled PBR socket) — must not keep the previous map.
      if (cur) return false;
    } else if (cur) {
      // Slot not driven by this graph eval — leftover maps (e.g. user unchecked Color/Normal)
      // used to look "unchanged" and the soft scalar path left the old weave on screen.
      return false;
    }
  }
  if (overrides.color instanceof THREE.Texture) {
    if (!texturesVisuallySame(mat.map, overrides.color)) return false;
  } else if (
    !(overrides.map instanceof THREE.Texture) &&
    overrides.map == null &&
    typeof overrides.color === 'string' &&
    mat.map
  ) {
    // Color is now a solid tint; albedo map must be cleared.
    return false;
  }
  const layers = overrides._materialLayers as Array<{
    mask?: THREE.Texture | null;
    map?: THREE.Texture | null;
    normalMap?: THREE.Texture | null;
    bumpMap?: THREE.Texture | null;
    roughnessMap?: THREE.Texture | null;
    metalnessMap?: THREE.Texture | null;
  }> | undefined;
  const luArr = (mat.userData as any)?._layerUniforms as unknown[] | undefined;
  if (layers?.length) {
    const layerUniforms = (mat.userData as any)?._layerUniforms as
      | Array<{ mask: { tex: { value: THREE.Texture | null } }; map: { tex: { value: THREE.Texture | null } }; normalMap: { tex: { value: THREE.Texture | null } }; bumpMap: { tex: { value: THREE.Texture | null } }; roughnessMap: { tex: { value: THREE.Texture | null } }; metalnessMap: { tex: { value: THREE.Texture | null } } }>
      | undefined;
    if (!layerUniforms || layerUniforms.length < Math.min(layers.length, 3)) return false;
    const count = Math.min(layers.length, 3);
    for (let i = 0; i < count; i++) {
      const l = layers[i]!;
      const u = layerUniforms[i]!;
      if (!texturesVisuallySame(u.mask.tex.value, l.mask)) return false;
      if (!texturesVisuallySame(u.map.tex.value, l.map)) return false;
      if (!texturesVisuallySame(u.normalMap.tex.value, l.normalMap)) return false;
      if (!texturesVisuallySame(u.bumpMap.tex.value, l.bumpMap)) return false;
      if (!texturesVisuallySame(u.roughnessMap.tex.value, l.roughnessMap)) return false;
      if (!texturesVisuallySame(u.metalnessMap.tex.value, l.metalnessMap)) return false;
    }
  } else if (luArr && luArr.length > 0) {
    return false;
  }
  return true;
}

function mapPresenceSignature(mat: THREE.MeshPhysicalMaterial): string {
  let s = '';
  for (const key of GRAPH_TEX_SLOT_KEYS) {
    s += (mat as any)[key] ? '1' : '0';
  }
  return s;
}

function layerMapPresenceKey(overrides: Record<string, any>): string {
  const layers = overrides._materialLayers as Array<{
    map?: THREE.Texture | null;
    normalMap?: THREE.Texture | null;
    bumpMap?: THREE.Texture | null;
    roughnessMap?: THREE.Texture | null;
    metalnessMap?: THREE.Texture | null;
  }> | undefined;
  if (!layers?.length) return '';
  const count = Math.min(layers.length, 3);
  let s = `n${count}`;
  for (let i = 0; i < count; i++) {
    const l = layers[i]!;
    s += `${l.map ? 'm' : ''}${l.normalMap ? 'n' : ''}${l.bumpMap ? 'b' : ''}${l.roughnessMap ? 'r' : ''}${l.metalnessMap ? 'e' : ''}`;
  }
  return s;
}

/**
 * Cap+Body share one material; Body must skip re-applying after Cap. The stamp MUST
 * include texture content — `String(texture)` is always "[object Object]", so wood and
 * gradient looked identical and Body (or a second Cap pass) early-returned while the
 * shared mat still held the previous maps (stuck gradient, leftover carbon roughness).
 */
function softOverrideStamp(
  shareKey: string | null,
  overrides: Record<string, any>,
  featureKey: string,
): string | null {
  if (!shareKey) return null;
  const colorPart =
    overrides.color instanceof THREE.Texture
      ? `ct:${textureContentKey(overrides.color)}`
      : typeof overrides.color === 'string'
        ? `c:${overrides.color}`
        : `c:${String(overrides.color ?? '')}`;
  const coat =
    `coat:${typeof overrides.clearcoat === 'number' ? overrides.clearcoat.toFixed(3) : 0}` +
    `:${typeof overrides.clearcoatRoughness === 'number' ? overrides.clearcoatRoughness.toFixed(3) : 0}`;
  const mapParts: string[] = [];
  for (const k of GRAPH_TEX_SLOT_KEYS) {
    const v = overrides[k];
    if (v instanceof THREE.Texture) mapParts.push(`${k}:${textureContentKey(v)}`);
    else if (typeof v === 'string' && v) mapParts.push(`${k}:u:${v}`);
    else if (v === null) mapParts.push(`${k}:0`);
    else mapParts.push(`${k}:-`);
  }
  return `${shareKey}|${colorPart}|${coat}|${featureKey}|${mapParts.join(';')}`;
}

/** True when two textures sample the same pixel source (UV clones of one bake / image). */
function texturesShareImage(
  a: THREE.Texture,
  b: THREE.Texture,
): boolean {
  if (a.image && b.image && a.image === b.image) return true;
  const sa = (a.image as { src?: string } | undefined)?.src;
  const sb = (b.image as { src?: string } | undefined)?.src;
  if (sa && sb && sa === sb) return true;
  const aUd = a.userData as Record<string, unknown> | undefined;
  const bUd = b.userData as Record<string, unknown> | undefined;
  const aSrc = (aUd?.__c3dBakeSource || aUd?.__c3dCloneSource || aUd?.__c3dProcessSource) as
    | THREE.Texture
    | undefined;
  const bSrc = (bUd?.__c3dBakeSource || bUd?.__c3dCloneSource || bUd?.__c3dProcessSource) as
    | THREE.Texture
    | undefined;
  if (aSrc && (aSrc === b || aSrc === bSrc || (aSrc.image && b.image && aSrc.image === b.image))) {
    return true;
  }
  if (bSrc && (bSrc === a || (bSrc.image && a.image && bSrc.image === a.image))) {
    return true;
  }
  if (aSrc && bSrc && (aSrc === bSrc || (aSrc.image && bSrc.image && aSrc.image === bSrc.image))) {
    return true;
  }
  return false;
}

/** True when `pending` is an unfinished CPU bake of `live` (same material, Exposure/HSL). */
function isInFlightBakeOf(pending: THREE.Texture, live: THREE.Texture): boolean {
  if (texturesShareImage(pending, live)) return true;
  const ud = pending.userData as Record<string, unknown> | undefined;
  if (!ud) return false;
  for (const ref of [ud.__c3dBakeSource, ud.__c3dProcessInput, ud.__c3dProcessSource, ud.__c3dBakeStandIn]) {
    if (ref instanceof THREE.Texture && (ref === live || texturesShareImage(ref, live))) return true;
  }
  return false;
}

function copyTextureUv(from: THREE.Texture, to: THREE.Texture): void {
  to.repeat.copy(from.repeat);
  to.offset.copy(from.offset);
  to.center.copy(from.center);
  to.rotation = from.rotation;
  to.wrapS = from.wrapS;
  to.wrapT = from.wrapT;
  commitTextureUV(to);
}

function textureUvIsIdentity(tex: THREE.Texture): boolean {
  return (
    Math.abs(tex.repeat.x - 1) < 1e-6 &&
    Math.abs(tex.repeat.y - 1) < 1e-6 &&
    Math.abs(tex.offset.x) < 1e-6 &&
    Math.abs(tex.offset.y) < 1e-6 &&
    Math.abs(tex.rotation) < 1e-6
  );
}

/**
 * Assign only when content differs — keeps GPU texture/program identity across graph re-evals.
 * Never replace a finished map with a white CPU-bake placeholder (metal tint flash).
 *
 * UV Transform tile/offset/rot changes clone the same image with a new matrix. The pending-bake
 * guard used to refuse those clones, so imported meshes kept the old tiling while the sphere
 * (PrimitiveMaterial always assigns) updated immediately.
 */
function applyTextureSlotColorSpace(key: string, tex: THREE.Texture): void {
  if (LINEAR_TEX_SLOTS.has(key)) {
    tex.colorSpace = THREE.LinearSRGBColorSpace;
  } else if (key === 'map' || key === 'emissiveMap') {
    tex.colorSpace = THREE.SRGBColorSpace;
  }
}

function assignTextureIfChanged(
  mat: THREE.MeshPhysicalMaterial,
  key: string,
  next: THREE.Texture | null,
): boolean {
  const cur = (mat as any)[key] as THREE.Texture | null | undefined;
  if (texturesVisuallySame(cur, next)) return false;

  if (cur && next && texturesShareImage(cur, next)) {
    // Same pixels, different UV (UV Transform tiles). Never copy identity UV onto a
    // live tiled map — that wiped Aniso Map tiling after the aniso CPU re-bake.
    if (textureUvIsIdentity(next) && !textureUvIsIdentity(cur)) {
      copyTextureUv(cur, next);
      applyTextureSlotColorSpace(key, next);
      (mat as any)[key] = next;
      return true;
    }
    copyTextureUv(next, cur);
    applyTextureSlotColorSpace(key, cur);
    return true;
  }

  if (isPendingCpuBake(next)) {
    // Same-map CPU bake (Metal tint / Exposure): keep finished pixels until the bake lands.
    if (cur && isInFlightBakeOf(next, cur) && !isBakePlaceholder(cur)) return false;
    // First load / other material: a 1×1 white placeholder keeps USE_*MAP compiled and
    // the tint/scalar look (blue carpaint, matte wood) until the bake is real.
    return holdPendingMapSlot(mat, key);
  }
  (mat as any)[key] = next;
  if (next) {
    applyTextureSlotColorSpace(key, next);
    commitTextureUV(next);
  }
  return true;
}

/** Push UV Transform tiling onto live maps even when the soft path would otherwise skip. */
function syncMaterialTextureUvsFromOverrides(
  mat: THREE.MeshPhysicalMaterial,
  overrides: Record<string, any>,
): boolean {
  let changed = false;
  const trySync = (slot: string, next: THREE.Texture | null | undefined) => {
    if (!(next instanceof THREE.Texture)) return;
    const cur = (mat as any)[slot] as THREE.Texture | null | undefined;
    if (!cur) return;
    if (texturesVisuallySame(cur, next)) return;
    if (!texturesShareImage(cur, next)) return;
    copyTextureUv(next, cur);
    changed = true;
  };
  for (const key of GRAPH_TEX_SLOT_KEYS) {
    trySync(key, overrides[key]);
  }
  if (overrides.color instanceof THREE.Texture) {
    trySync('map', overrides.color);
  }
  return changed;
}

function applyTransmissionAttenuationFromOverrides(
  mat: THREE.MeshPhysicalMaterial,
  overrides: Record<string, unknown>,
  mesh: THREE.Mesh,
): void {
  if ((mat.transmission ?? 0) <= 0.001) return;
  applyTransmissionAttenuation(mat, {
    meshName: mesh.name,
    parentName: mesh.parent?.name,
    attenuationColor: typeof overrides.attenuationColor === 'string' ? overrides.attenuationColor : undefined,
    attenuationDistance:
      typeof overrides.attenuationDistance === 'number' ? overrides.attenuationDistance : undefined,
  });
  // Belt-and-suspenders: never leave Dist ≤ 0 on the GPU material.
  mat.attenuationDistance = sanitizeAttenuationDistance(mat.attenuationDistance);
}

function applySkipToneMap(mat: THREE.Material, overrides: { skipToneMap?: boolean }): void {
  // Always write — leaving the previous Graph's `toneMapped = false` on Wood/Carpaint.
  mat.toneMapped = overrides.skipToneMap !== true;
}

function applyGraphScalarOverrides(
  mat: THREE.MeshPhysicalMaterial,
  overrides: Record<string, any>,
  mesh?: THREE.Mesh,
): void {
  const uvMeta = overrides[C3D_UV_RANDOMIZE_UD];
  if (uvMeta && typeof uvMeta === 'object') {
    (mat.userData as any)[C3D_UV_RANDOMIZE_UD] = uvMeta;
  } else {
    // Straw (and similar) stamp Per-mesh UV ranges here; Gradient has no UV Randomizer.
    // Leaving the stamp makes array instance-UV keep Straw offsets after the swap.
    delete (mat.userData as any)[C3D_UV_RANDOMIZE_UD];
  }
  if (overrides.color instanceof THREE.Texture) {
    // Albedo is map-driven; keep white tint.
    safeColorSet(mat.color, '#ffffff');
  } else if (overrides.color && typeof overrides.color === 'string') {
    safeColorSet(mat.color, overrides.color);
  }
  // Always write core PBR scalars — omitted keys left Carbon/Gradient roughness & metalness on Wood.
  mat.roughness = typeof overrides.roughness === 'number' ? overrides.roughness : 0.5;
  mat.metalness = typeof overrides.metalness === 'number' ? overrides.metalness : 0;
  if (overrides.emissive) safeColorSet(mat.emissive, overrides.emissive);
  else safeColorSet(mat.emissive, '#000000');
  mat.emissiveIntensity =
    typeof overrides.emissiveIntensity === 'number' ? overrides.emissiveIntensity : 1;
  applySkipToneMap(mat, overrides);
  applyCoatStackFromOverrides(mat, overrides);
  mat.ior = typeof overrides.ior === 'number' ? overrides.ior : 1.5;
  // Always write — omitting left the previous material's anisotropy on Wood / Gradient.
  mat.anisotropy = typeof overrides.anisotropy === 'number' ? overrides.anisotropy : 0;
  mat.anisotropyRotation =
    typeof overrides.anisotropyRotation === 'number' ? overrides.anisotropyRotation : 0;
  if (!(overrides.anisotropyMap instanceof THREE.Texture)) {
    mat.anisotropyMap = null;
  }
  mat.thickness = typeof overrides.thickness === 'number' ? overrides.thickness : 0;
  // Do not write Absorb Dist ≤ 0 onto the material — Three.js divides by it.
  if (overrides.attenuationColor) safeColorSet(mat.attenuationColor, overrides.attenuationColor);
  if (overrides.attenuationDistance != null && overrides.attenuationDistance > 0) {
    mat.attenuationDistance = overrides.attenuationDistance;
  } else if (overrides.attenuationDistance != null && overrides.attenuationDistance <= 0) {
    mat.attenuationDistance = Infinity;
  }
  if (overrides.specularIntensity != null) mat.specularIntensity = overrides.specularIntensity;
  else mat.specularIntensity = 1;
  if (overrides.specularColor) safeColorSet(mat.specularColor, overrides.specularColor);
  else safeColorSet(mat.specularColor, '#ffffff');
  mat.envMapIntensity =
    typeof overrides.envMapIntensity === 'number' ? overrides.envMapIntensity : 1;
  // Never inherit iridescence / sheen maps from a prior graph material.
  mat.iridescence = typeof overrides.iridescence === 'number' ? overrides.iridescence : 0;
  mat.iridescenceMap = null;
  // Specular maps from GLB / prior Physical mats survive clearcoat resets and read as
  // wrong gloss on Wood (scalar roughness alone cannot override them).
  if (!(overrides.specularIntensityMap instanceof THREE.Texture)) {
    mat.specularIntensityMap = null;
  }
  if (!(overrides.specularColorMap instanceof THREE.Texture)) {
    mat.specularColorMap = null;
  }
  if (!(overrides.sheenColorMap instanceof THREE.Texture)) {
    mat.sheenColorMap = null;
  }
  if (!(overrides.sheenRoughnessMap instanceof THREE.Texture)) {
    mat.sheenRoughnessMap = null;
  }
  // Roughness/metalness maps only when the graph drives them — GLB seeds otherwise
  // multiply a dark "roughness" into a glossy result even at roughness = 1.
  if (!(overrides.roughnessMap instanceof THREE.Texture)) {
    mat.roughnessMap = null;
  }
  if (!(overrides.metalnessMap instanceof THREE.Texture)) {
    mat.metalnessMap = null;
  }
  if (overrides.bumpScale != null) mat.bumpScale = overrides.bumpScale;
  else mat.bumpScale = 1;
  if (overrides.normalScale != null) {
    const ns = overrides.normalScale as number;
    mat.normalScale.set(ns, ns);
  } else {
    mat.normalScale.set(1, 1);
  }
  if (overrides.opacity != null && !((overrides.transmission ?? 0) > 0)) {
    mat.opacity = overrides.opacity;
  } else if (overrides.opacity == null && !((overrides.transmission ?? 0) > 0)) {
    mat.opacity = 1;
  }

  // Live uniforms — no shader rebuild required.
  if (overrides._falloff) {
    const fo = (mat.userData as any)?._falloffUniforms as
      | { front: { value: THREE.Color }; side: { value: THREE.Color }; pow: { value: number } }
      | undefined;
    if (fo) {
      const { front, side, power } = overrides._falloff as { front: string; side: string; power: number };
      fo.front.value.set(isValidColor(front) ? front : '#ffffff');
      fo.side.value.set(isValidColor(side) ? side : '#000000');
      fo.pow.value = power;
    }
  }
  if (overrides._materialLayers) {
    const layers = overrides._materialLayers as Array<{
      color: string;
      bumpScale: number;
      roughness: number;
      metalness: number;
      amount: number;
      mask?: THREE.Texture | null;
      map?: THREE.Texture | null;
      normalMap?: THREE.Texture | null;
      bumpMap?: THREE.Texture | null;
      roughnessMap?: THREE.Texture | null;
      metalnessMap?: THREE.Texture | null;
    }>;
    const luArr = (mat.userData as any)?._layerUniforms as Array<{
      color: { value: THREE.Color };
      bumpScale: { value: number };
      roughness: { value: number };
      metalness: { value: number };
      amount: { value: number };
      mask: { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
      map: { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
      normalMap: { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
      bumpMap: { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
      roughnessMap: { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
      metalnessMap: { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
    }> | undefined;
    if (luArr) {
      const count = Math.min(layers.length, 3, luArr.length);
      const syncSlot = (
        slot: { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } },
        tex: THREE.Texture | null | undefined,
      ) => {
        slot.tex.value = tex ?? null;
        if (tex) {
          tex.updateMatrix();
          slot.uvMat.value.copy(tex.matrix);
        } else {
          slot.uvMat.value.identity();
        }
      };
      for (let i = 0; i < count; i++) {
        const l = layers[i]!;
        luArr[i]!.color.value.set(isValidColor(l.color) ? l.color : '#ffffff');
        luArr[i]!.bumpScale.value = l.bumpScale ?? 1;
        luArr[i]!.roughness.value = l.roughness;
        luArr[i]!.metalness.value = l.metalness;
        luArr[i]!.amount.value = l.amount;
        syncSlot(luArr[i]!.mask, l.mask);
        syncSlot(luArr[i]!.map, l.map);
        syncSlot(luArr[i]!.normalMap, l.normalMap);
        syncSlot(luArr[i]!.bumpMap, l.bumpMap);
        syncSlot(luArr[i]!.roughnessMap, l.roughnessMap);
        syncSlot(luArr[i]!.metalnessMap, l.metalnessMap);
      }
    }
  }
  if (mesh) applyTransmissionAttenuationFromOverrides(mat, overrides, mesh);
}

/**
 * Safely copy properties from any Standard/Physical material into a MeshPhysicalMaterial.
 * THREE's built-in copy() throws when source is MeshStandardMaterial because it tries
 * to access Physical-only properties (clearcoatNormalScale, etc.) that don't exist.
 * This copies each property individually and never throws.
 */
function copyMaterialSafe(target: THREE.MeshPhysicalMaterial, source: THREE.Material): void {
  if (!(source instanceof THREE.MeshStandardMaterial)) return;

  // MeshStandardMaterial properties
  target.color.copy(source.color);
  target.roughness = source.roughness;
  target.metalness = source.metalness;
  target.map = source.map;
  target.lightMap = source.lightMap;
  target.lightMapIntensity = source.lightMapIntensity;
  target.aoMap = source.aoMap;
  target.aoMapIntensity = source.aoMapIntensity;
  target.emissive.copy(source.emissive);
  target.emissiveIntensity = source.emissiveIntensity;
  target.emissiveMap = source.emissiveMap;
  target.bumpMap = source.bumpMap;
  target.bumpScale = source.bumpScale;
  target.normalMap = source.normalMap;
  target.normalMapType = source.normalMapType;
  target.normalScale.copy(source.normalScale);
  target.displacementMap = source.displacementMap;
  target.displacementScale = source.displacementScale;
  target.displacementBias = source.displacementBias;
  target.roughnessMap = source.roughnessMap;
  target.metalnessMap = source.metalnessMap;
  target.alphaMap = source.alphaMap;
  target.envMap = source.envMap;
  target.envMapRotation.copy(source.envMapRotation);
  target.envMapIntensity = source.envMapIntensity;
  target.wireframe = source.wireframe;
  target.flatShading = source.flatShading;
  target.fog = source.fog;

  // Common Material properties
  target.opacity = source.opacity;
  target.transparent = source.transparent;
  target.side = source.side;
  target.depthTest = source.depthTest;
  target.depthWrite = source.depthWrite;
  target.alphaTest = source.alphaTest;
  target.visible = source.visible;
  target.toneMapped = source.toneMapped;

  // MeshPhysicalMaterial-specific properties (only if source is Physical)
  if (source instanceof THREE.MeshPhysicalMaterial) {
    target.clearcoat = source.clearcoat;
    target.clearcoatMap = source.clearcoatMap;
    target.clearcoatRoughness = source.clearcoatRoughness;
    target.clearcoatRoughnessMap = source.clearcoatRoughnessMap;
    target.clearcoatNormalMap = source.clearcoatNormalMap;
    target.clearcoatNormalScale.copy(source.clearcoatNormalScale);
    target.ior = source.ior;
    target.transmission = source.transmission;
    target.transmissionMap = source.transmissionMap;
    target.thickness = source.thickness;
    target.thicknessMap = source.thicknessMap;
    target.anisotropy = source.anisotropy;
    target.anisotropyRotation = source.anisotropyRotation;
    target.anisotropyMap = source.anisotropyMap;
    target.iridescence = source.iridescence;
    target.iridescenceMap = source.iridescenceMap;
    target.iridescenceIOR = source.iridescenceIOR;
    target.iridescenceThicknessRange = [...source.iridescenceThicknessRange] as [number, number];
    target.sheen = source.sheen;
    target.sheenColor.copy(source.sheenColor);
    target.sheenColorMap = source.sheenColorMap;
    target.sheenRoughness = source.sheenRoughness;
    target.sheenRoughnessMap = source.sheenRoughnessMap;
    target.specularIntensity = source.specularIntensity;
    target.specularIntensityMap = source.specularIntensityMap;
    target.specularColor.copy(source.specularColor);
    target.specularColorMap = source.specularColorMap;
    target.attenuationDistance = source.attenuationDistance;
    target.attenuationColor.copy(source.attenuationColor);
    target.dispersion = source.dispersion;
  }
}

/**
 * FBXLoader (and similar) emit MeshPhongMaterial / MeshLambertMaterial. In a viewport lit
 * mainly by scene environment maps, those materials often stay nearly black because they
 * do not pick up IBL like MeshStandardMaterial — geometry looks "missing" even though it loads.
 */
function legacyMaterialToStandard(m: THREE.MeshPhongMaterial | THREE.MeshLambertMaterial): THREE.MeshStandardMaterial {
  const std = new THREE.MeshStandardMaterial();
  std.name = m.name;
  std.color.copy(m.color);
  std.map = m.map;
  std.lightMap = m.lightMap;
  std.lightMapIntensity = m.lightMapIntensity;
  std.aoMap = m.aoMap;
  std.aoMapIntensity = m.aoMapIntensity;
  std.emissive.copy(m.emissive);
  std.emissiveMap = m.emissiveMap;
  std.emissiveIntensity = m.emissiveIntensity;
  std.bumpMap = m.bumpMap;
  std.bumpScale = m.bumpScale;
  std.normalMap = m.normalMap;
  std.normalMapType = m.normalMapType;
  std.normalScale.copy(m.normalScale);
  std.displacementMap = m.displacementMap;
  std.displacementScale = m.displacementScale;
  std.displacementBias = m.displacementBias;
  std.alphaMap = m.alphaMap;
  std.envMap = m.envMap;
  std.envMapRotation.copy(m.envMapRotation);
  std.envMapIntensity = (m as unknown as { envMapIntensity?: number }).envMapIntensity ?? 1;
  std.wireframe = m.wireframe;
  std.flatShading = m.flatShading;
  std.fog = m.fog;
  std.opacity = m.opacity;
  std.transparent = m.transparent;
  std.side = m.side;
  std.depthTest = m.depthTest;
  std.depthWrite = m.depthWrite;
  std.alphaTest = m.alphaTest;

  std.metalness = 0;
  if (m instanceof THREE.MeshPhongMaterial) {
    const shininess = m.shininess ?? 30;
    std.roughness = THREE.MathUtils.clamp(150 / (shininess + 150), 0.04, 1);
  } else {
    std.roughness = 1;
  }

  m.dispose();
  return std;
}

function upgradeLegacyShadedMaterials(root: THREE.Object3D): void {
  root.traverse((obj) => {
    if (!(obj instanceof THREE.Mesh)) return;
    const mat = obj.material;
    if (Array.isArray(mat)) {
      obj.material = mat.map((sub) =>
        sub instanceof THREE.MeshPhongMaterial || sub instanceof THREE.MeshLambertMaterial
          ? legacyMaterialToStandard(sub)
          : sub,
      );
    } else if (mat instanceof THREE.MeshPhongMaterial || mat instanceof THREE.MeshLambertMaterial) {
      obj.material = legacyMaterialToStandard(mat);
    }
  });
}

function applyFbxStyleSceneFixes(root: THREE.Object3D): void {
  root.updateMatrixWorld(true);
  root.traverse((child) => {
    if (child instanceof THREE.SkinnedMesh && child.skeleton) {
      child.skeleton.pose();
    }
  });
}

// ---------------------------------------------------------------------------
// Material transition helpers
// ---------------------------------------------------------------------------
/** Slots that define visible appearance; if any change, lerping scalars against new maps causes bogus “third” colors. */
const PBR_TEXTURE_SIGNATURE_SLOTS = [
  'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
  'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
  'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
  'specularIntensityMap', 'specularColorMap',
  'sheenColorMap', 'sheenRoughnessMap',
] as const;

type PbrTextureSlot = (typeof PBR_TEXTURE_SIGNATURE_SLOTS)[number];
type PbrTextureMap = Partial<Record<PbrTextureSlot, THREE.Texture | null>>;

function textureContentKey(tex: THREE.Texture | null | undefined): string {
  if (!tex) return '';
  const img = tex.image as { src?: string; width?: number; height?: number } | undefined;
  const src = typeof img?.src === 'string' ? img.src : '';
  // Prefer shared image URL / instance over clone uuid — graph eval re-clones on every
  // selection. Canvas/procedural maps have no src; stamp a stable id on the image object
  // so UV clones of the same bake don't look like a map-set change (which killed color lerps).
  let id = src;
  if (!id && tex.image && typeof tex.image === 'object') {
    const im = tex.image as Record<string, unknown>;
    if (typeof im.__c3dImgKey !== 'string') {
      im.__c3dImgKey = `img:${tex.uuid}`;
    }
    id = im.__c3dImgKey as string;
  }
  if (!id) id = tex.uuid;
  return [
    id,
    tex.repeat.x,
    tex.repeat.y,
    tex.offset.x,
    tex.offset.y,
    tex.rotation,
    tex.channel ?? 0,
    tex.wrapS,
    tex.wrapT,
  ].join('|');
}

/** True when two textures show the same pixels + UV transform (ignores clone uuid churn from graph eval). */
function texturesVisuallySame(a: THREE.Texture | null | undefined, b: THREE.Texture | null | undefined): boolean {
  if (a === b) return true;
  if (!a || !b) return false;
  if (a.image && b.image && a.image === b.image) {
    return (
      a.repeat.equals(b.repeat) &&
      a.offset.equals(b.offset) &&
      a.rotation === b.rotation &&
      (a.channel ?? 0) === (b.channel ?? 0) &&
      a.wrapS === b.wrapS &&
      a.wrapT === b.wrapT
    );
  }
  // HTMLImageElement with same URL
  const sa = (a.image as { src?: string } | undefined)?.src;
  const sb = (b.image as { src?: string } | undefined)?.src;
  if (sa && sb && sa === sb) {
    return (
      a.repeat.equals(b.repeat) &&
      a.offset.equals(b.offset) &&
      a.rotation === b.rotation &&
      (a.channel ?? 0) === (b.channel ?? 0) &&
      a.wrapS === b.wrapS &&
      a.wrapT === b.wrapT
    );
  }
  return false;
}

function materialTextureSignatureFromMap(textures: PbrTextureMap): string {
  let s = '';
  for (const k of PBR_TEXTURE_SIGNATURE_SLOTS) {
    s += `${k}:${textureContentKey(textures[k])};`;
  }
  return s;
}

function materialTextureSignature(mat: THREE.MeshPhysicalMaterial): string {
  return materialTextureSignatureFromMap(snapTexturesFromMat(mat));
}

interface PBRSnapshot {
  color: THREE.Color;
  roughness: number;
  metalness: number;
  emissive: THREE.Color;
  emissiveIntensity: number;
  clearcoat: number;
  clearcoatRoughness: number;
  transmission: number;
  opacity: number;
  ior: number;
  thickness: number;
  specularIntensity: number;
  specularColor: THREE.Color;
  envMapIntensity: number;
  /** Identity of bound textures — scalar crossfade is only safe when this matches. */
  texSig: string;
  /** Bound textures for this snapshot (enables deferred swap when texSig changes). */
  textures?: PbrTextureMap;
}

type MeshTransitionEntry = {
  from: PBRSnapshot;
  to: PBRSnapshot;
  mat: THREE.MeshPhysicalMaterial;
  fromExtras: MaterialExtrasSnap;
  toExtras: MaterialExtrasSnap;
};

type PendingMaterialHold = {
  from: PBRSnapshot;
  to: PBRSnapshot;
  fromExtras?: MaterialExtrasSnap;
  toExtras?: MaterialExtrasSnap;
  /** Incoming library maps aren't ready — keep the bound material until flush re-applies. */
  keepLiveUntilReady?: boolean;
};

type LayerScalarSnap = {
  color: THREE.Color;
  bumpScale: number;
  roughness: number;
  metalness: number;
  amount: number;
};

type FalloffSnap = {
  front: THREE.Color;
  side: THREE.Color;
  pow: number;
};

type MaterialExtrasSnap = {
  layers: LayerScalarSnap[] | null;
  falloff: FalloffSnap | null;
};

function emptyExtras(): MaterialExtrasSnap {
  return { layers: null, falloff: null };
}

function snapMaterialExtras(mat: THREE.MeshPhysicalMaterial): MaterialExtrasSnap {
  const fo = (mat.userData as any)?._falloffUniforms as
    | { front: { value: THREE.Color }; side: { value: THREE.Color }; pow: { value: number } }
    | undefined;
  const luArr = (mat.userData as any)?._layerUniforms as Array<{
    color: { value: THREE.Color };
    bumpScale: { value: number };
    roughness: { value: number };
    metalness: { value: number };
    amount: { value: number };
  }> | undefined;

  let falloff: FalloffSnap | null = null;
  if (fo) {
    falloff = {
      front: fo.front.value.clone(),
      side: fo.side.value.clone(),
      pow: fo.pow.value,
    };
  }
  let layers: LayerScalarSnap[] | null = null;
  if (luArr?.length) {
    layers = luArr.map((u) => ({
      color: u.color.value.clone(),
      bumpScale: u.bumpScale.value,
      roughness: u.roughness.value,
      metalness: u.metalness.value,
      amount: u.amount.value,
    }));
  }
  return { layers, falloff };
}

function applyMaterialExtras(mat: THREE.MeshPhysicalMaterial, extras: MaterialExtrasSnap) {
  if (extras.falloff) {
    const fo = (mat.userData as any)?._falloffUniforms as
      | { front: { value: THREE.Color }; side: { value: THREE.Color }; pow: { value: number } }
      | undefined;
    if (fo) {
      fo.front.value.copy(extras.falloff.front);
      fo.side.value.copy(extras.falloff.side);
      fo.pow.value = extras.falloff.pow;
    }
  }
  if (extras.layers) {
    const luArr = (mat.userData as any)?._layerUniforms as Array<{
      color: { value: THREE.Color };
      bumpScale: { value: number };
      roughness: { value: number };
      metalness: { value: number };
      amount: { value: number };
    }> | undefined;
    if (luArr) {
      const n = Math.min(extras.layers.length, luArr.length);
      for (let i = 0; i < n; i++) {
        const l = extras.layers[i]!;
        const u = luArr[i]!;
        u.color.value.copy(l.color);
        u.bumpScale.value = l.bumpScale;
        u.roughness.value = l.roughness;
        u.metalness.value = l.metalness;
        u.amount.value = l.amount;
      }
    }
  }
}

function lerpMaterialExtras(
  mat: THREE.MeshPhysicalMaterial,
  from: MaterialExtrasSnap,
  to: MaterialExtrasSnap,
  t: number,
) {
  if (from.falloff && to.falloff) {
    const fo = (mat.userData as any)?._falloffUniforms as
      | { front: { value: THREE.Color }; side: { value: THREE.Color }; pow: { value: number } }
      | undefined;
    if (fo) {
      fo.front.value.copy(from.falloff.front).lerp(to.falloff.front, t);
      fo.side.value.copy(from.falloff.side).lerp(to.falloff.side, t);
      fo.pow.value = THREE.MathUtils.lerp(from.falloff.pow, to.falloff.pow, t);
    }
  } else if (to.falloff && t >= 1) {
    applyMaterialExtras(mat, { layers: null, falloff: to.falloff });
  }
  if (from.layers && to.layers) {
    const luArr = (mat.userData as any)?._layerUniforms as Array<{
      color: { value: THREE.Color };
      bumpScale: { value: number };
      roughness: { value: number };
      metalness: { value: number };
      amount: { value: number };
    }> | undefined;
    if (luArr) {
      const n = Math.min(from.layers.length, to.layers.length, luArr.length);
      for (let i = 0; i < n; i++) {
        const a = from.layers[i]!;
        const b = to.layers[i]!;
        const u = luArr[i]!;
        u.color.value.copy(a.color).lerp(b.color, t);
        u.bumpScale.value = THREE.MathUtils.lerp(a.bumpScale, b.bumpScale, t);
        u.roughness.value = THREE.MathUtils.lerp(a.roughness, b.roughness, t);
        u.metalness.value = THREE.MathUtils.lerp(a.metalness, b.metalness, t);
        u.amount.value = THREE.MathUtils.lerp(a.amount, b.amount, t);
      }
    }
  } else if (to.layers && t >= 1) {
    applyMaterialExtras(mat, { layers: to.layers, falloff: null });
  }
}

/** Smoother than cubic — readable as a real blend, not a snap. */
function transitionEase(t: number): number {
  const x = Math.min(1, Math.max(0, t));
  return x * x * x * (x * (x * 6 - 15) + 10);
}

function snapTexturesFromMat(mat: THREE.MeshPhysicalMaterial): PbrTextureMap {
  const textures: PbrTextureMap = {};
  for (const k of PBR_TEXTURE_SIGNATURE_SLOTS) {
    textures[k] = ((mat as any)[k] as THREE.Texture | null | undefined) ?? null;
  }
  return textures;
}

function buildTargetSnapshot(
  mat: THREE.MeshPhysicalMaterial,
  pending?: Record<string, THREE.Texture> | null,
): PBRSnapshot {
  const textures = snapTexturesFromMat(mat);
  if (pending) {
    for (const [k, tex] of Object.entries(pending)) {
      if ((PBR_TEXTURE_SIGNATURE_SLOTS as readonly string[]).includes(k)) {
        textures[k as PbrTextureSlot] = tex;
      }
    }
  }
  const base = snapPBR(mat);
  return {
    ...base,
    textures,
    texSig: materialTextureSignatureFromMap(textures),
  };
}

function snapPBR(mat: THREE.MeshPhysicalMaterial): PBRSnapshot {
  return {
    color: mat.color?.clone() ?? new THREE.Color(1, 1, 1),
    roughness: mat.roughness ?? 0.5,
    metalness: mat.metalness ?? 0,
    emissive: mat.emissive?.clone() ?? new THREE.Color(0, 0, 0),
    emissiveIntensity: mat.emissiveIntensity ?? 1,
    clearcoat: mat.clearcoat ?? 0,
    clearcoatRoughness: mat.clearcoatRoughness ?? 0,
    transmission: mat.transmission ?? 0,
    opacity: mat.opacity ?? 1,
    ior: mat.ior ?? 1.5,
    thickness: mat.thickness ?? 0,
    specularIntensity: mat.specularIntensity ?? 1,
    specularColor: mat.specularColor?.clone() ?? new THREE.Color(1, 1, 1),
    envMapIntensity: mat.envMapIntensity ?? 1,
    texSig: materialTextureSignature(mat),
    textures: snapTexturesFromMat(mat),
  };
}

function applySnap(mat: THREE.MeshPhysicalMaterial, s: PBRSnapshot, withTextures = true) {
  if (withTextures && s.textures) {
    for (const k of PBR_TEXTURE_SIGNATURE_SLOTS) {
      if (Object.prototype.hasOwnProperty.call(s.textures, k)) {
        (mat as any)[k] = s.textures[k] ?? null;
      }
    }
  }
  if (mat.color) mat.color.copy(s.color); else mat.color = s.color.clone();
  mat.roughness = s.roughness;
  mat.metalness = s.metalness;
  if (mat.emissive) mat.emissive.copy(s.emissive); else mat.emissive = s.emissive.clone();
  mat.emissiveIntensity = s.emissiveIntensity;
  mat.opacity = s.opacity;
  applyAlphaMaskFlags(mat, s.opacity);
  mat.envMapIntensity = s.envMapIntensity;
  if (mat instanceof THREE.MeshPhysicalMaterial) {
    mat.clearcoat = s.clearcoat;
    mat.clearcoatRoughness = s.clearcoatRoughness;
    mat.transmission = s.transmission;
    mat.ior = s.ior;
    mat.thickness = s.thickness;
    mat.specularIntensity = s.specularIntensity;
    if (mat.specularColor) mat.specularColor.copy(s.specularColor);
  }
}

function lerpSnap(mat: THREE.MeshPhysicalMaterial, from: PBRSnapshot, to: PBRSnapshot, t: number) {
  if (mat.color) mat.color.copy(from.color).lerp(to.color, t);
  mat.roughness = THREE.MathUtils.lerp(from.roughness, to.roughness, t);
  mat.metalness = THREE.MathUtils.lerp(from.metalness, to.metalness, t);
  if (mat.emissive) mat.emissive.copy(from.emissive).lerp(to.emissive, t);
  mat.emissiveIntensity = THREE.MathUtils.lerp(from.emissiveIntensity, to.emissiveIntensity, t);
  const opacity = THREE.MathUtils.lerp(from.opacity, to.opacity, t);
  mat.opacity = opacity;
  applyAlphaMaskFlags(mat, opacity);
  mat.envMapIntensity = THREE.MathUtils.lerp(from.envMapIntensity, to.envMapIntensity, t);
  if (mat instanceof THREE.MeshPhysicalMaterial) {
    mat.clearcoat = THREE.MathUtils.lerp(from.clearcoat, to.clearcoat, t);
    mat.clearcoatRoughness = THREE.MathUtils.lerp(from.clearcoatRoughness, to.clearcoatRoughness, t);
    mat.transmission = THREE.MathUtils.lerp(from.transmission, to.transmission, t);
    mat.ior = THREE.MathUtils.lerp(from.ior, to.ior, t);
    mat.thickness = THREE.MathUtils.lerp(from.thickness, to.thickness, t);
    mat.specularIntensity = THREE.MathUtils.lerp(from.specularIntensity, to.specularIntensity, t);
    if (mat.specularColor) mat.specularColor.copy(from.specularColor).lerp(to.specularColor, t);
  }
}

const SNAP_EPS = 0.001;
function snapsEqual(a: PBRSnapshot, b: PBRSnapshot): boolean {
  return (
    a.texSig === b.texSig &&
    a.color.equals(b.color) &&
    Math.abs(a.roughness - b.roughness) < SNAP_EPS &&
    Math.abs(a.metalness - b.metalness) < SNAP_EPS &&
    a.emissive.equals(b.emissive) &&
    Math.abs(a.emissiveIntensity - b.emissiveIntensity) < SNAP_EPS &&
    Math.abs(a.clearcoat - b.clearcoat) < SNAP_EPS &&
    Math.abs(a.clearcoatRoughness - b.clearcoatRoughness) < SNAP_EPS &&
    Math.abs(a.transmission - b.transmission) < SNAP_EPS &&
    Math.abs(a.opacity - b.opacity) < SNAP_EPS &&
    Math.abs(a.ior - b.ior) < SNAP_EPS &&
    Math.abs(a.thickness - b.thickness) < SNAP_EPS &&
    Math.abs(a.specularIntensity - b.specularIntensity) < SNAP_EPS &&
    a.specularColor.equals(b.specularColor) &&
    Math.abs(a.envMapIntensity - b.envMapIntensity) < SNAP_EPS
  );
}

function interpolateSnap(from: PBRSnapshot, to: PBRSnapshot, t: number): PBRSnapshot {
  return {
    color: from.color.clone().lerp(to.color, t),
    roughness: THREE.MathUtils.lerp(from.roughness, to.roughness, t),
    metalness: THREE.MathUtils.lerp(from.metalness, to.metalness, t),
    emissive: from.emissive.clone().lerp(to.emissive, t),
    emissiveIntensity: THREE.MathUtils.lerp(from.emissiveIntensity, to.emissiveIntensity, t),
    clearcoat: THREE.MathUtils.lerp(from.clearcoat, to.clearcoat, t),
    clearcoatRoughness: THREE.MathUtils.lerp(from.clearcoatRoughness, to.clearcoatRoughness, t),
    transmission: THREE.MathUtils.lerp(from.transmission, to.transmission, t),
    opacity: THREE.MathUtils.lerp(from.opacity, to.opacity, t),
    ior: THREE.MathUtils.lerp(from.ior, to.ior, t),
    thickness: THREE.MathUtils.lerp(from.thickness, to.thickness, t),
    specularIntensity: THREE.MathUtils.lerp(from.specularIntensity, to.specularIntensity, t),
    specularColor: from.specularColor.clone().lerp(to.specularColor, t),
    envMapIntensity: THREE.MathUtils.lerp(from.envMapIntensity, to.envMapIntensity, t),
    texSig: from.texSig,
    textures: from.textures,
  };
}

function collectMeshNames(obj: THREE.Object3D): string[] {
  const names: string[] = [];
  obj.traverse((child) => {
    if (child instanceof THREE.Mesh && child.name) {
      names.push(child.name);
    }
  });
  return names;
}

/** Detect FBX assets so we use FBXLoader instead of GLTFLoader (avoids "Kaydara FBX is not valid JSON"). */
function isFbxAssetUrl(url: string): boolean {
  const pathOnly = url.split('?')[0].split('#')[0];
  return /\.fbx$/i.test(pathOnly);
}

/** GLTFLoader must only run on glTF / GLB — preloading JPG etc. parses binary as JSON and throws ("JFIF" / invalid JSON). */
function isGltfOrGlbAssetUrl(url: string): boolean {
  const pathOnly = url.split('?')[0].split('#')[0];
  return /\.(glb|gltf)$/i.test(pathOnly);
}

/**
 * Prime drei's GLTF cache for URLs that may mount later (rule visibility).
 * Only `.glb` / `.gltf` (FBX and textures skipped — scene objects may reference any asset type).
 */
export function prefetchSceneModelAssetUrls(urls: readonly string[]): void {
  const seen = new Set<string>();
  for (const url of urls) {
    if (!url || seen.has(url)) continue;
    seen.add(url);
    if (isFbxAssetUrl(url)) continue;
    if (!isGltfOrGlbAssetUrl(url)) continue;
    try {
      useGLTF.preload(url, true, true, extendGltfLoader as any);
    } catch {
      /* ignore */
    }
  }
}

type GlbModelLoadedCoreProps = GlbModelProps & {
  sourceScene: THREE.Object3D;
  sourceAnimations: THREE.AnimationClip[];
};

const GlbModelLoadedCore = forwardRef<GlbModelHandle, GlbModelLoadedCoreProps>(function GlbModelLoadedCore(
  {
    sourceScene: scene,
    sourceAnimations: animations,
    url,
    position = [0, 0, 0],
    rotation = [0, 0, 0],
    scale = 1,
    materialOverrides,
    perMeshOverrides,
    meshVisibility,
    // highlightMeshName is intentionally unused — selection is via postprocessing outline
    visible = true,
    castShadow = true,
    receiveShadow = true,
    onClick,
    onPointerOver,
    onPointerOut,
    animation,
    onAnimationsLoaded,
    onLoaded,
    onContentReady,
    holdUntilReady = false,
    preferProgressiveMaterials = false,
    materialsGateReady = true,
    onMeshNamesReady,
    wireframe = false,
    displayMode = 'default',
    materialGraphOverrides = null,
    transitionDuration = 0,
    transitionStartedAt,
    opacityTransitionDuration,
    opacityOverride,
    onFadeOutComplete,
    excludeFromSSAO = false,
    excludeFromRefraction = false,
    depthOffset = 0,
    stayOnTop = false,
  },
  ref: React.Ref<GlbModelHandle>,
) {
  const textureQualityEpoch = useSyncExternalStore(
    subscribePlayerTextureQuality,
    getPlayerTextureQualityEpoch,
    getPlayerTextureQualityEpoch,
  );
  const groupRef = useRef<THREE.Group>(null);
  const { actions, names } = useAnimations(animations, groupRef);
  const loadedRef = useRef(false);

  const prevSnapRef = useRef(new Map<string, PBRSnapshot>());
  const transRef = useRef(new Map<string, MeshTransitionEntry>());
  const graphMatsRef = useRef(new Map<string, THREE.MeshPhysicalMaterial>());
  // Deferred texture slots (graph + per-mesh) until GPU-uploadable image data exists.
  const pendingTexRef = useRef(new Map<THREE.Material, Record<string, THREE.Texture>>());
  /** While maps load, keep previous look; flush applies `to` in one step (no sequential map pops). */
  const pendingHoldRef = useRef(new Map<string, PendingMaterialHold>());
  const alphaHiddenRef = useRef(new Set<THREE.Mesh>());
  const alphaHiddenAtRef = useRef(new WeakMap<THREE.Mesh, number>());
  const ALPHA_HIDE_FALLBACK_MS = 4000;
  const mountedRef = useRef(false);
  const durationRef = useRef(transitionDuration);
  durationRef.current = transitionDuration;
  const opacityDurationRef = useRef(
    opacityTransitionDuration != null ? opacityTransitionDuration : transitionDuration,
  );
  opacityDurationRef.current =
    opacityTransitionDuration != null ? opacityTransitionDuration : transitionDuration;
  const transitionStartedAtRef = useRef(transitionStartedAt);
  transitionStartedAtRef.current = transitionStartedAt;

  const fadeTargetRef = useRef<number | undefined>(undefined);
  const fadeCurrentRef = useRef(1);
  const fadeProgressRef = useRef(1);
  const fadeCallbackRef = useRef(onFadeOutComplete);
  fadeCallbackRef.current = onFadeOutComplete;

  const safePosition: [number, number, number] = [
    position?.[0] ?? 0,
    position?.[1] ?? 0,
    position?.[2] ?? 0,
  ];
  const safeRotation: [number, number, number] = [
    rotation?.[0] ?? 0,
    rotation?.[1] ?? 0,
    rotation?.[2] ?? 0,
  ];
  const safeScale: [number, number, number] = Array.isArray(scale)
    ? [scale[0] ?? 1, scale[1] ?? 1, scale[2] ?? 1]
    : [scale ?? 1, scale ?? 1, scale ?? 1];

  const clonedScene = useMemo(() => {
    const cloned = scene.clone(true);
    cloned.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const geom = child.geometry as THREE.BufferGeometry;
      if (!geom.hasAttribute('uv')) {
        for (const name of ['uv2', 'uv1', 'uv3']) {
          if (geom.hasAttribute(name)) {
            geom.setAttribute('uv', geom.getAttribute(name));
            break;
          }
        }
      }
      // Always discard exporter TANGENT accessors. Many GLBs ship wrong/zero tangents;
      // keeping them enables USE_TANGENT with a broken TBN so anisotropy looks isotropic
      // while primitives (no authored tangents → getTangentFrame) look correct.
      ensureTangents(child, { force: true });
    });
    detachEmbeddedTexturesForPlayer(cloned);
    upgradeLegacyShadedMaterials(cloned);
    if (isFbxAssetUrl(url)) applyFbxStyleSceneFixes(cloned);
    return cloned;
  }, [scene, textureQualityEpoch, url]);

  // Snapshot of original (unmodified) materials so graph overrides always
  // start from the pristine model state, not from a previously modified material.
  const originalMatsRef = useRef<Map<string, THREE.Material>>(new Map());
  useMemo(() => {
    const map = new Map<string, THREE.Material>();
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.name && child.material && !Array.isArray(child.material)) {
        map.set(child.name, child.material.clone());
      }
    });
    originalMatsRef.current = map;
  }, [clonedScene]);
  const { gl, invalidate, scene: r3fScene } = useThree();

  function abortMeshTransition(meshName: string, mat?: THREE.MeshPhysicalMaterial) {
    transRef.current.delete(meshName);
    if (mat) (mat.userData as any).__c3dMatTransitioning = false;
  }

  /** Instant apply — used for map swaps so Wood never soft-lerps from Carbon scalars. */
  function hardSnapMaterial(
    meshName: string,
    mat: THREE.MeshPhysicalMaterial,
    to: PBRSnapshot,
    toExtras?: MaterialExtrasSnap,
  ) {
    abortMeshTransition(meshName, mat);
    applySnap(mat, to, true);
    if (toExtras) applyMaterialExtras(mat, toExtras);
    else applyMaterialExtras(mat, emptyExtras());
    // Coat / roughness defines must refresh — otherwise Wood can keep Carpaint's
    // clearcoat program path and look shiny at roughness 1.
    mat.needsUpdate = true;
    prevSnapRef.current.set(meshName, to);
  }

  function meshTransition(
    meshName: string,
    mat: THREE.MeshPhysicalMaterial,
    explicitFrom?: PBRSnapshot,
    explicitFromExtras?: MaterialExtrasSnap,
    explicitTo?: PBRSnapshot,
    explicitToExtras?: MaterialExtrasSnap,
  ) {
    const duration = durationRef.current;
    const to = explicitTo ?? snapPBR(mat);
    const toExtras = explicitToExtras ?? snapMaterialExtras(mat);

    // Editor never arms the shared Cap/Body clock — without this, soft updates restore
    // `from` and stay stuck at t=0 forever (Coat Rough / tint sliders appear dead).
    const useSharedClock = isMaterialTransitionClockActive();

    if (duration <= 0 || !mountedRef.current || !useSharedClock) {
      hardSnapMaterial(meshName, mat, to, toExtras);
      return;
    }

    const prev = explicitFrom ?? prevSnapRef.current.get(meshName);
    if (!prev) {
      hardSnapMaterial(meshName, mat, to, toExtras);
      return;
    }

    const existing = transRef.current.get(meshName);
    let from: PBRSnapshot;
    let fromExtras: MaterialExtrasSnap;
    if (existing) {
      const { t: eased } = materialTransitionBlend();
      from = interpolateSnap(existing.from, existing.to, eased);
      fromExtras = existing.fromExtras;
    } else {
      from = prev;
      fromExtras = explicitFromExtras ?? emptyExtras();
    }

    if (snapsEqual(from, to)) {
      hardSnapMaterial(meshName, mat, to, toExtras);
      return;
    }

    // Different map sets (e.g. Gradient → Carbon Fiber): do not lerp. Soft-lerp was leaving
    // the new maps on the GPU while scalars eased from the previous look — clearcoat 0→1 read
    // as a shiny coat flashing on before the carbon weave settled.
    //
    // Color / falloff / layer tints DO lerp (Metal Yellow→Brown, attribute swatches). Snapping
    // those removed visible color transitions. Cap+Body path-dependence is handled by deferring
    // soft stamps until the shared clock finishes, not by skipping the lerp.
    if (from.texSig !== to.texSig) {
      hardSnapMaterial(meshName, mat, to, toExtras);
      return;
    }

    // Same maps but different "structure" scalars: hard-snap. Soft-lerp restored `from`
    // (Carpaint clearcoat / low roughness) onto Wood for the whole blend — and with a shared
    // clock that can sit at t≈0 across Cap/Body re-applies, Wood stayed lacquered forever
    // even when the graph said roughness 1 / clearcoat 0. Tint-only updates still lerp below.
    if (
      Math.abs(from.roughness - to.roughness) > 0.02 ||
      Math.abs(from.metalness - to.metalness) > 0.02 ||
      Math.abs(from.clearcoat - to.clearcoat) > 0.01 ||
      Math.abs(from.clearcoatRoughness - to.clearcoatRoughness) > 0.02 ||
      Math.abs(from.specularIntensity - to.specularIntensity) > 0.05 ||
      Math.abs(from.envMapIntensity - to.envMapIntensity) > 0.05 ||
      Math.abs(from.ior - to.ior) > 0.02
    ) {
      hardSnapMaterial(meshName, mat, to, toExtras);
      return;
    }

    // Same texture set + same structure: restore previous tint, then lerp color extras.
    applySnap(mat, from, true);
    applyMaterialExtras(mat, fromExtras);
    (mat.userData as any).__c3dMatTransitioning = true;
    prevSnapRef.current.set(meshName, to);
    transRef.current.set(meshName, { from, to, mat, fromExtras, toExtras });
  }

  useEffect(() => {
    const maxAniso = gl.capabilities.getMaxAnisotropy();
    if (maxAniso <= 1) return;
    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const m of mats) {
        if (!m || !isStandardLikeMaterial(m)) continue;
        const texSlots = [
          'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
          'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
        ] as const;
        for (const slot of texSlots) {
          const tex = (m as THREE.MeshStandardMaterial & { anisotropyMap?: THREE.Texture | null })[slot] as
            | THREE.Texture
            | null
            | undefined;
          if (tex && textureHasUploadableData(tex) && tex.anisotropy !== maxAniso) {
            tex.anisotropy = maxAniso;
            tex.needsUpdate = true;
          }
        }
      }
    });
  }, [clonedScene, gl]);

  const meshNames = useMemo(() => collectMeshNames(clonedScene), [clonedScene]);

  useImperativeHandle(ref, () => ({
    getMeshNames: () => meshNames,
    getGroup: () => groupRef.current,
  }), [meshNames]);

  const onLoadedRef = useRef(onLoaded);
  onLoadedRef.current = onLoaded;
  const onContentReadyRef = useRef(onContentReady);
  onContentReadyRef.current = onContentReady;
  const contentReadyRef = useRef(false);
  /** Once we've painted a complete look, never blank the group on attribute / graph swaps. */
  const contentReadyOnceRef = useRef(false);
  const [contentReady, setContentReady] = useState(!holdUntilReady);
  const [graphApplyGen, setGraphApplyGen] = useState(0);
  const graphApplyGenRef = useRef(0);

  useEffect(() => {
    // New asset: must wait again before showing.
    contentReadyOnceRef.current = false;
    contentReadyRef.current = false;
    if (holdUntilReady) setContentReady(false);
    else setContentReady(true);
  }, [clonedScene, holdUntilReady]);

  useEffect(() => {
    // Graph overrides often arrive after the first GLB parse — re-hold only until the first
    // complete paint. After that, pending maps stay deferred without restoring the prior look.
    if (!holdUntilReady) return;
    // Rules/materials not settled yet — stay hidden. Premature ready on empty overrides left
    // contentReadyOnce set, blocked re-hold, and the product built up from white GLB defaults.
    if (!materialsGateReady) {
      contentReadyOnceRef.current = false;
      contentReadyRef.current = false;
      setContentReady(false);
      return;
    }
    if (contentReadyOnceRef.current) return;
    contentReadyRef.current = false;
    setContentReady(false);
  }, [materialGraphOverrides, holdUntilReady, materialsGateReady]);

  useEffect(() => {
    if (scene && !loadedRef.current) {
      loadedRef.current = true;
      onLoadedRef.current?.();
      invalidate();
    }
  }, [scene, invalidate]);

  const markContentReady = useCallback(() => {
    if (contentReadyRef.current) return;
    if (holdUntilReady && !materialsGateReady) return;
    contentReadyRef.current = true;
    contentReadyOnceRef.current = true;
    setContentReady(true);
    onContentReadyRef.current?.();
  }, [holdUntilReady, materialsGateReady]);

  const onMeshNamesReadyRef = useRef(onMeshNamesReady);
  onMeshNamesReadyRef.current = onMeshNamesReady;

  useEffect(() => {
    if (meshNames.length > 0) {
      onMeshNamesReadyRef.current?.(meshNames);
    }
  }, [meshNames]);

  const onAnimationsLoadedRef = useRef(onAnimationsLoaded);
  onAnimationsLoadedRef.current = onAnimationsLoaded;

  useEffect(() => {
    if (names.length > 0 && onAnimationsLoadedRef.current) {
      onAnimationsLoadedRef.current(names);
    }
  }, [names]);

  useEffect(() => {
    if (!actions || names.length === 0) return;

    const clipName = animation?.clipName || names[0];
    const action = actions[clipName];
    if (!action) return;

    if (animation?.playing !== false) {
      action.reset().play();
      action.timeScale = animation?.speed ?? 1;
    } else {
      action.paused = true;
    }

    return () => { action.stop(); };
  }, [actions, names, animation?.playing, animation?.clipName, animation?.speed]);

  // Apply simple (legacy) material overrides — reuse existing material to avoid shader recompilation
  useLayoutEffect(() => {
    if (!materialOverrides || displayMode !== 'default') return;
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && isStandardLikeMaterial(child.material) && !Array.isArray(child.material)) {
        if (meshUsesMaterialGraph(materialGraphOverrides, child.name || undefined)) return;
        const mat = child.material as THREE.MeshPhysicalMaterial;
        if (materialOverrides.color) safeColorSet(mat.color, materialOverrides.color);
        if (materialOverrides.roughness != null) mat.roughness = materialOverrides.roughness;
        if (materialOverrides.metalness != null) mat.metalness = materialOverrides.metalness;
        if (materialOverrides.emissive) safeColorSet(mat.emissive, materialOverrides.emissive);
        if (materialOverrides.emissiveIntensity != null) mat.emissiveIntensity = materialOverrides.emissiveIntensity;
        if (materialOverrides.clearcoat != null) mat.clearcoat = materialOverrides.clearcoat;
        meshTransition(child.name || child.uuid, mat);
      }
    });
    invalidate();
  }, [clonedScene, materialOverrides, displayMode, materialGraphOverrides]);

  // Apply per-mesh PBR overrides (skip when a display mode like UV checker is active)
  useLayoutEffect(() => {
    if (!perMeshOverrides || displayMode !== 'default') return;

    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.name) return;
      // Skip meshes covered by materialGraphOverrides — those take precedence
      if (meshUsesMaterialGraph(materialGraphOverrides, child.name || undefined)) return;
      const overrides = perMeshOverrides[child.name];
      if (!overrides) return;

      const raw = child.material;
      if (!raw || Array.isArray(raw)) return;

      const fromSnap =
        raw instanceof THREE.MeshPhysicalMaterial ? snapPBR(raw) : undefined;

      const mat = new THREE.MeshPhysicalMaterial();
      if (isStandardLikeMaterial(raw)) {
        copyMaterialSafe(mat, raw);
      }

      if (overrides.color) safeColorSet(mat.color, overrides.color);
      if (overrides.roughness != null) mat.roughness = overrides.roughness;
      if (overrides.metalness != null) mat.metalness = overrides.metalness;
      if (overrides.emissive) safeColorSet(mat.emissive, overrides.emissive);
      if (overrides.emissiveIntensity != null) mat.emissiveIntensity = overrides.emissiveIntensity;
      applySkipToneMap(mat, overrides);
      applyCoatStackFromOverrides(mat, overrides);
      if (overrides.opacity != null) {
        mat.opacity = overrides.opacity;
        if (overrides.opacity < 1 && !((overrides.transmission ?? 0) > 0)) {
          mat.transparent = true;
          mat.depthWrite = false;
          const opVal = overrides.opacity;
          const prevObc = mat.onBeforeCompile;
          mat.onBeforeCompile = (shader, renderer) => {
            if (prevObc) prevObc(shader, renderer);
            shader.uniforms.u_opacityOverride = { value: opVal };
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <common>',
              '#include <common>\nuniform float u_opacityOverride;',
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <dithering_fragment>',
              'gl_FragColor.a *= u_opacityOverride;\n#include <dithering_fragment>',
            );
          };
          mat.customProgramCacheKey = () => `opacity_${opVal}_${mat.uuid}`;
        }
      }
      if (overrides.transmission != null && overrides.transmission > 0) {
        mat.transparent = false;
        mat.depthWrite = true;
        mat.side = THREE.FrontSide;
        if ((mat.opacity ?? 1) < 0.999) mat.opacity = 1;
        const blurVal = overrides.transmitBlur ?? 0;
        applyTransmitBlurDefine(mat, blurVal);
        applyTransmissionAttenuationFromOverrides(mat, overrides, child);
      }
      if (overrides.ior != null) mat.ior = overrides.ior;
      // Always write anisotropy — omitted keys inherited Carbon Fiber / metal weave on Wood.
      mat.anisotropy = typeof overrides.anisotropy === 'number' ? overrides.anisotropy : 0;
      if (mat.anisotropy > 0 || overrides.anisotropyMap) {
        ensureAnisotropyTangents(child);
      }
      mat.anisotropyRotation =
        typeof overrides.anisotropyRotation === 'number' ? overrides.anisotropyRotation : 0;
      if (overrides.thickness != null) mat.thickness = overrides.thickness;
      if (overrides.attenuationColor) safeColorSet(mat.attenuationColor, overrides.attenuationColor);
      if (overrides.attenuationDistance != null && overrides.attenuationDistance > 0) {
        mat.attenuationDistance = overrides.attenuationDistance;
      } else if (overrides.attenuationDistance != null && overrides.attenuationDistance <= 0) {
        mat.attenuationDistance = Infinity;
      }
      if (overrides.specularIntensity != null) mat.specularIntensity = overrides.specularIntensity;
      if (overrides.specularColor) safeColorSet(mat.specularColor, overrides.specularColor);
      if (overrides.envMapIntensity != null) mat.envMapIntensity = overrides.envMapIntensity;
      if ((overrides as any).normalScale != null) {
        const ns = (overrides as any).normalScale;
        mat.normalScale.set(ns, ns);
      }

      // Texture loading
      const textureSlots: Array<[keyof PBRMeshOverrideData, keyof THREE.MeshPhysicalMaterial]> = [
        ['map', 'map'],
        ['normalMap', 'normalMap'],
        ['bumpMap', 'bumpMap'],
        ['roughnessMap', 'roughnessMap'],
        ['metalnessMap', 'metalnessMap'],
        ['emissiveMap', 'emissiveMap'],
        ['aoMap', 'aoMap'],
        ['displacementMap', 'displacementMap'],
        ['alphaMap', 'alphaMap'],
        ['anisotropyMap', 'anisotropyMap'],
      ];

      for (const [key, matKey] of textureSlots) {
        const texUrl = overrides[key] as string | null | undefined;
        if (texUrl) {
          const tex = loadTexture(texUrl, LINEAR_TEX_SLOTS.has(key as string));
          if (textureHasUploadableData(tex)) {
            (mat as any)[matKey] = tex;
          } else {
            deferPendingTexture(pendingTexRef.current, mat, matKey as string, tex);
          }
        }
      }

      const repeat = toVec2Pair(overrides.mapRepeat);
      const offset = toVec2Pair(overrides.mapOffset);
      const rot = overrides.mapRotation != null && Number.isFinite(overrides.mapRotation) ? overrides.mapRotation : null;

      if (repeat || offset || rot != null) {
        const applyTransform = (tex: THREE.Texture | null) => {
          if (!tex) return;
          if (repeat) tex.repeat.set(repeat[0], repeat[1]);
          if (offset) tex.offset.set(offset[0], offset[1]);
          if (rot != null) tex.rotation = rot;
          tex.wrapS = THREE.RepeatWrapping;
          tex.wrapT = THREE.RepeatWrapping;
          if (textureHasUploadableData(tex)) tex.needsUpdate = true;
        };
        applyTransform(mat.map);
        applyTransform(mat.normalMap);
        applyTransform(mat.bumpMap);
        applyTransform(mat.roughnessMap);
        applyTransform(mat.metalnessMap);
        applyTransform(mat.emissiveMap);
        applyTransform(mat.aoMap);
        applyTransform(mat.displacementMap);
        applyTransform(mat.alphaMap);
        applyTransform(mat.anisotropyMap ?? null);
      }

      syncAnisotropyMapStrength(mat, child);

      applyAlphaMaskFlags(mat, overrides.opacity ?? mat.opacity ?? 1);

      const falloffData = (overrides as any)?._falloff;
      if (falloffData) {
        const { front, side, power } = falloffData;
        const uFront = { value: new THREE.Color(isValidColor(front) ? front : '#ffffff') };
        const uSide = { value: new THREE.Color(isValidColor(side) ? side : '#000000') };
        const uPow = { value: power };
        const prevObc2 = mat.onBeforeCompile;
        mat.onBeforeCompile = (shader, renderer) => {
          if (prevObc2) prevObc2(shader, renderer);
          shader.uniforms.u_falloffFront = uFront;
          shader.uniforms.u_falloffSide = uSide;
          shader.uniforms.u_falloffPow = uPow;
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <normal_fragment_maps>',
            `#include <normal_fragment_maps>
{
  float falloffNdotV = max(dot(normal, normalize(vViewPosition)), 0.0);
  float falloffFactor = pow(1.0 - falloffNdotV, u_falloffPow);
  vec3 _foBase = mix(u_falloffFront, u_falloffSide, falloffFactor);
#ifdef USE_MAP
  vec4 _foMap = texture2D(map, vMapUv);
  diffuseColor.rgb = _foBase * _foMap.rgb;
#else
  diffuseColor.rgb = _foBase;
#endif
}`,
          );
          shader.fragmentShader =
            'uniform vec3 u_falloffFront;\nuniform vec3 u_falloffSide;\nuniform float u_falloffPow;\n' +
            shader.fragmentShader;
        };
        mat.customProgramCacheKey = () => `falloff_${mat.uuid}`;
      }

      const roundedEdgeData = (overrides as any)?._roundedEdge;
      if (roundedEdgeData && child instanceof THREE.Mesh && ensureSmoothedNormals(child.geometry)) {
        const uRadius = { value: roundedEdgeData.radius ?? 5 };
        const prevObc3 = mat.onBeforeCompile;
        mat.onBeforeCompile = (shader, renderer) => {
          if (prevObc3) prevObc3(shader, renderer);
          shader.uniforms.u_roundedEdgeRadius = uRadius;
          shader.vertexShader = 'attribute vec3 smoothNormal;\nvarying vec3 vSmoothNormal;\n' + shader.vertexShader;
          shader.vertexShader = shader.vertexShader.replace(
            '#include <defaultnormal_vertex>',
            '#include <defaultnormal_vertex>\nvSmoothNormal = normalize(normalMatrix * smoothNormal);',
          );
          shader.fragmentShader = 'uniform float u_roundedEdgeRadius;\nvarying vec3 vSmoothNormal;\n' + shader.fragmentShader;
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <normal_fragment_maps>',
            ROUNDED_EDGE_FRAG,
          );
        };
        mat.customProgramCacheKey = () => `roundedEdge_${mat.uuid}`;
      }

      if (applyGraphEnvOverride(mat, overrides, r3fScene.environment, invalidate)) {
        mat.needsUpdate = true;
      }

      mat.needsUpdate = true;
      child.material = mat;
      meshTransition(child.name, mat, fromSnap);
      syncInstancedMeshMaterialFromSource(child);
    });
    demoteNestedTransmission(clonedScene);
  }, [clonedScene, perMeshOverrides, displayMode, materialGraphOverrides]);

  // Apply material graph overrides (takes precedence over per-mesh overrides).
  // Restore originals for meshes that lost their overrides — otherwise removing a
  // target/tag leaves the last graph material stuck on the Three.js mesh.
  useLayoutEffect(() => {
    // Restore any previously hidden meshes before re-processing
    for (const mesh of alphaHiddenRef.current) mesh.visible = true;
    alphaHiddenRef.current = new Set();
    alphaHiddenAtRef.current = new WeakMap();

    const graphActive = !!materialGraphOverrides && displayMode === 'default';

    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.name) return;
      const stillOverridden = graphActive
        ? !!(materialGraphOverrides![child.name] ?? materialGraphOverrides!['*'])
        : false;
      if (stillOverridden) return;
      if (!graphMatsRef.current.has(child.name) && !child.userData.c3dGraphMatApplied) return;
      const original = originalMatsRef.current.get(child.name);
      if (original) {
        child.material = original;
        syncInstancedMeshMaterialFromSource(child);
      }
      const stale = graphMatsRef.current.get(child.name);
      if (stale) pendingTexRef.current.delete(stale);
      graphMatsRef.current.delete(child.name);
      child.userData.c3dGraphMatApplied = false;
    });

    if (!graphActive) {
      invalidate();
      return;
    }

    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.name) return;
      const overrides = materialGraphOverrides![child.name] ?? materialGraphOverrides!['*'];
      if (!overrides) return;

      // Always copy from the ORIGINAL model material, not the current
      // (potentially modified) one, so unwired graph slots revert cleanly.
      const original = originalMatsRef.current.get(child.name) ?? child.material;
      if (!original || Array.isArray(original)) return;

      const hasTransmission = overrides.transmission != null && overrides.transmission > 0;
      const isDebugPreview = overrides._debugPreview === true;

      // Reuse previously created material for this mesh when possible to
      // avoid shader recompilation on every attribute change.
      const prevHadTransmission = (child.material instanceof THREE.MeshPhysicalMaterial) &&
        child.material.transmission > 0;
      const shaderChanged = hasTransmission !== prevHadTransmission;
      const shareKey =
        typeof (overrides as { __c3dShareKey?: unknown }).__c3dShareKey === 'string'
          ? (overrides as { __c3dShareKey: string }).__c3dShareKey
          : null;
      let mat = graphMatsRef.current.get(child.name);
      const prevGraphMat = mat;
      if (shareKey) {
        // Cap + Body that receive the same library graph share one MeshPhysicalMaterial —
        // separate mats are why Cap clearcoat (carpaint) paints the new tint before Body.
        if (shaderChanged) {
          if (prevGraphMat) pendingTexRef.current.delete(prevGraphMat);
          invalidateSharedGraphMaterial(shareKey);
        }
        mat = getSharedGraphMaterial(shareKey, () => new THREE.MeshPhysicalMaterial());
        // Migrate first adopter's current look onto the shared slot so we don't flash white.
        if (!(mat.userData as any).c3dShareSeeded) {
          const live =
            (prevGraphMat && isStandardLikeMaterial(prevGraphMat) ? prevGraphMat : null) ||
            (child.userData.c3dGraphMatApplied && isStandardLikeMaterial(child.material as THREE.Material)
              ? (child.material as THREE.MeshPhysicalMaterial)
              : null);
          if (live && live !== mat) {
            copyMaterialSafe(mat, live);
            // Share-seed copies the previous look for continuity — never keep its lacquer
            // or glossy base scalars; the graph apply below owns the full PBR stack.
            resetCoatStack(mat);
            mat.roughness = 1;
            mat.metalness = 0;
            mat.specularIntensity = 1;
            mat.specularIntensityMap = null;
            mat.specularColorMap = null;
            mat.roughnessMap = null;
            mat.metalnessMap = null;
            mat.envMapIntensity = 1;
          }
          (mat.userData as any).c3dShareSeeded = true;
        }
        graphMatsRef.current.set(child.name, mat);
      } else if (!mat || shaderChanged) {
        if (prevGraphMat) pendingTexRef.current.delete(prevGraphMat);
        mat = new THREE.MeshPhysicalMaterial();
        graphMatsRef.current.set(child.name, mat);
      }
      // Shell / GPU modifiers must patch this in place (see ModifiedMesh) or Cap freezes
      // on a clone while Body array instances keep lerping — Cap paints first.
      (mat.userData as any).c3dPreferInPlaceModPatch = true;

      // After first paint, never bind a new library look (Straw) until its maps are
      // GPU-ready. The 1×1 white placeholders used to flash on every finish swap.
      // Mobile skips this hold — deferred wood maps never flushing left the prior finish forever.
      if (
        !preferProgressiveMaterials &&
        contentReadyOnceRef.current &&
        child.userData.c3dGraphMatApplied &&
        isStandardLikeMaterial(child.material as THREE.Material) &&
        !isDebugPreview &&
        queueUnreadyGraphTextures(pendingTexRef.current, mat, overrides)
      ) {
        pendingHoldRef.current.set(child.name, {
          from: snapPBR(child.material as THREE.MeshPhysicalMaterial),
          to: snapPBR(child.material as THREE.MeshPhysicalMaterial),
          keepLiveUntilReady: true,
        });
        invalidate();
        return;
      }

      const reusingGraphMat = !!(mat && !shaderChanged && child.userData.c3dGraphMatApplied);
      const fromSnapForTransition =
        mat && !shaderChanged ? snapPBR(mat) : undefined;
      const fromExtrasForTransition =
        mat && !shaderChanged ? snapMaterialExtras(mat) : undefined;

      // Same shader features: never wipe onBeforeCompile / force recompile.
      // Gold↔silver (and colorToTexture swatches) mint new CanvasTextures each eval —
      // that used to take the full path and flash bright until the program rebuilt (~0.5s).
      const featureKey = graphFeatureCacheKey(overrides);
      const featuresMatch =
        reusingGraphMat &&
        !isDebugPreview &&
        (mat.userData as any).c3dGraphFeaturesKey === featureKey;
      const layerPresence = layerMapPresenceKey(overrides);
      const layerPresenceOk =
        layerPresence === '' ||
        (mat.userData as any).c3dLayerPresenceKey === layerPresence;

      if (featuresMatch && layerPresenceOk) {
        if (graphTexturesUnchanged(mat, overrides)) {
          const softStamp = softOverrideStamp(shareKey, overrides, featureKey);
          // Shared Cap+Body mat: Cap already applied — Body must not re-apply (flash).
          // Never skip while a lerp is in flight: stamp used to be set at arm-time while the
          // shared mat was still on the previous tint (Yellow), so Brown after Yellow stuck dirty.
          if (
            softStamp &&
            (mat.userData as any).c3dSoftStamp === softStamp &&
            !(mat.userData as any).__c3dMatTransitioning &&
            // Don't skip while a raw-diffuse HSL stand-in is still on the shared mat.
            !GRAPH_TEX_SLOT_KEYS.some((k) => {
              const t = (mat as any)[k] as THREE.Texture | null | undefined;
              const ud = t?.userData as Record<string, unknown> | undefined;
              return !!(ud?.__c3dIsBakeStandInDisplay || ud?.__c3dBakePlaceholder);
            })
          ) {
            // Stamp can match while UV Transform tiles still need to land (in-place sync).
            syncMaterialTextureUvsFromOverrides(mat, overrides);
            if (applyGraphEnvOverride(mat, overrides, r3fScene.environment, invalidate)) {
              mat.needsUpdate = true;
            }
            child.material = mat;
            if ((mat.anisotropy ?? 0) > 0 || mat.anisotropyMap) {
              ensureAnisotropyTangents(child);
              if (mat.anisotropyMap) {
                promotePendingCpuProcessTexture(mat.anisotropyMap);
                syncAnisotropyMapStrength(mat, child);
              }
            }
            syncInstancedMeshMaterialFromSource(child);
            child.userData.c3dGraphMatApplied = true;
            return;
          }
          applyGraphScalarOverrides(mat, overrides, child);
          syncMaterialTextureUvsFromOverrides(mat, overrides);
          if (applyGraphEnvOverride(mat, overrides, r3fScene.environment, invalidate)) {
            mat.needsUpdate = true;
          }
          const toSnap = snapPBR(mat);
          const toExtras = snapMaterialExtras(mat);
          meshTransition(child.name, mat, fromSnapForTransition, fromExtrasForTransition, toSnap, toExtras);
          // Anisotropy / coat define flips must rebuild the program — scalar-only soft
          // path used to leave USE_ANISOTROPY off until a full map swap.
          if ((mat.anisotropy ?? 0) > 0 || mat.anisotropyMap) {
            ensureAnisotropyTangents(child);
            syncAnisotropyMapStrength(mat, child);
            mat.needsUpdate = true;
          }
          // Stamp only once the shared mat is at the target (tint snaps immediately; scalar
          // lerps clear __c3dMatTransitioning in useFrame when raw >= 1).
          if (softStamp && !(mat.userData as any).__c3dMatTransitioning) {
            (mat.userData as any).c3dSoftStamp = softStamp;
          } else if (softStamp) {
            (mat.userData as any).c3dSoftStampPending = softStamp;
            delete (mat.userData as any).c3dSoftStamp;
          }
          ensureTangentsForMappedMaterial(child, mat, overrides);
          child.material = mat;
          syncInstancedMeshMaterialFromSource(child);
          child.userData.c3dGraphMatApplied = true;
          return;
        }

        // Soft map swap: keep compiled program; only needsUpdate when map presence flips.
        // Invalidate soft stamp — texture content changed; Cap/Body must not skip.
        delete (mat.userData as any).c3dSoftStamp;
        delete (mat.userData as any).c3dSoftStampPending;
        const presenceBefore = mapPresenceSignature(mat);
        const softTexSlots = [
          'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
          'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
          'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
          'specularIntensityMap', 'specularColorMap',
          'sheenColorMap', 'sheenRoughnessMap',
          'transmissionMap', 'thicknessMap', 'iridescenceMap',
        ] as const;
        const softGraphSlots = new Set<string>();
        for (const key of softTexSlots) {
          if (overrides[key] != null) softGraphSlots.add(key);
        }
        if (overrides.color instanceof THREE.Texture) softGraphSlots.add('map');
        for (const k of softTexSlots) {
          const cur = (mat as any)[k] as THREE.Texture | null | undefined;
          if (!cur) continue;
          if (!softGraphSlots.has(k)) {
            (mat as any)[k] = null;
            continue;
          }
          const incoming = overrides[k];
          if (
            incoming instanceof THREE.Texture &&
            (incoming.userData as Record<string, unknown> | undefined)?.__c3dBakePending &&
            !isInFlightBakeOf(incoming, cur)
          ) {
            holdPendingMapSlot(mat, k);
          }
        }

        if (overrides.color instanceof THREE.Texture) {
          const tex = overrides.color;
          promotePendingCpuProcessTexture(tex);
          if (textureHasUploadableData(tex)) {
            if (!textureHasStableMinFilter(tex)) configureStableTextureSampling(tex);
            assignTextureIfChanged(mat, 'map', tex);
          } else {
            deferPendingTexture(pendingTexRef.current, mat, 'map', tex);
            // Still push Tile/Offset/Rot onto the live map while the bake finishes.
            syncMaterialTextureUvsFromOverrides(mat, overrides);
          }
        }

        applyGraphScalarOverrides(mat, overrides, child);

        for (const key of softTexSlots) {
          const val = overrides[key];
          let tex: THREE.Texture | null = null;
          if (val instanceof THREE.Texture) tex = val;
          else if (typeof val === 'string' && val) tex = loadTexture(val, LINEAR_TEX_SLOTS.has(key));
          if (!tex) continue;
          promotePendingCpuProcessTexture(tex);
          if (textureHasUploadableData(tex)) {
            if (!textureHasStableMinFilter(tex)) configureStableTextureSampling(tex);
            assignTextureIfChanged(mat, key, tex);
          } else {
            deferPendingTexture(pendingTexRef.current, mat, key, tex);
            syncMaterialTextureUvsFromOverrides(mat, overrides);
          }
        }

        const presenceAfter = mapPresenceSignature(mat);
        if (presenceBefore !== presenceAfter) {
          mat.needsUpdate = true;
        }

        const softPending = pendingTexRef.current.get(mat);
        const softPendingSlots = softPending ? Object.keys(softPending).length : 0;
        pendingHoldRef.current.delete(child.name);
        if (softPendingSlots > 0) {
          // Keep the NEW scalars / stand-in maps visible. Restoring `fromSnap` showed Carbon
          // Fiber (wrong UVs) for ~0.5s whenever Wood's first CPU bake was still running.
          const toSnap = buildTargetSnapshot(mat, softPending!);
          const toExtras = snapMaterialExtras(mat);
          abortMeshTransition(child.name, mat);
          pendingHoldRef.current.set(child.name, {
            from: snapPBR(mat),
            to: toSnap,
            fromExtras: snapMaterialExtras(mat),
            toExtras,
          });
          invalidate();
        } else {
          const toSnap = snapPBR(mat);
          const toExtras = snapMaterialExtras(mat);
          if (
            fromSnapForTransition &&
            fromSnapForTransition.texSig !== toSnap.texSig
          ) {
            hardSnapMaterial(child.name, mat, toSnap, toExtras);
          } else {
            meshTransition(child.name, mat, fromSnapForTransition, fromExtrasForTransition, toSnap, toExtras);
          }
        }
        ensureTangentsForMappedMaterial(child, mat, overrides);
        syncAnisotropyMapStrength(mat, child);
        if (applyGraphEnvOverride(mat, overrides, r3fScene.environment, invalidate)) {
          mat.needsUpdate = true;
        }
        child.material = mat;
        syncInstancedMeshMaterialFromSource(child);
        child.userData.c3dGraphMatApplied = true;
        (mat.userData as any).c3dLayerPresenceKey = layerPresence;
        // Stamp only once maps are actually on the mat (pending flush sets it later).
        if (softPendingSlots === 0) {
          const softStamp = softOverrideStamp(shareKey, overrides, featureKey);
          if (softStamp) (mat.userData as any).c3dSoftStamp = softStamp;
        }
        return;
      }

      // Reused graph mats already have a compiled program. Wiping onBeforeCompile
      // on every library swap (Wood every select) forced a 1-frame glossy/dark shader.
      if (!reusingGraphMat) {
        mat.onBeforeCompile = () => {};
      }
      delete (mat.userData as any).c3dSoftStamp;
      delete (mat.userData as any).c3dSoftStampPending;
      // Preserve env map across resets to avoid async shader recompilation
      const savedEnvMap = mat.envMap;
      const savedEnvSig = (mat.userData as any).c3dEnvOverrideKey as string | undefined;
      // Debug preview: start from a clean matte material so original GLB metal /
      // transmission / maps don't wash out the inspected node output as white.
      if (isDebugPreview) {
        mat.color.set('#ffffff');
        mat.roughness = 1;
        mat.metalness = 0;
        mat.envMapIntensity = 0;
        mat.envMap = null;
        mat.transmission = 0;
        mat.thickness = 0;
        mat.clearcoat = 0;
        mat.clearcoatRoughness = 0;
        mat.emissive.set('#000000');
        mat.emissiveIntensity = 0;
        mat.opacity = 1;
        mat.transparent = false;
        mat.depthWrite = true;
        mat.ior = 1.5;
        mat.sheen = 0;
      } else if (!reusingGraphMat && !hasTransmission && isStandardLikeMaterial(original)) {
        // Only seed from the GLB original when creating the graph material.
        // Re-copying on every color swipe briefly rebuilds from the often-lighter
        // base and reads as a brighter → darker two-step change for carpaint.
        copyMaterialSafe(mat, original);
      }
      // Restore env map if the graph uses envOverride (avoids async shader recompilation)
      const envSig = envOverrideKey(overrides._envOverride);
      if (!isDebugPreview && envSig && savedEnvMap && savedEnvSig === envSig) {
        mat.envMap = savedEnvMap;
      }

      const texSlotKeys = [
        'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
        'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
        'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
        'specularIntensityMap', 'specularColorMap',
        'sheenColorMap', 'sheenRoughnessMap',
        'transmissionMap', 'thicknessMap', 'iridescenceMap',
      ];
      // Clear slots the graph does not drive. If it *does* drive a slot but the new
      // map is still baking, drop a leftover map from the previous library material
      // (Carpaint roughness staying on Wood until the HSL bake lands).
      const graphTexturedSlots = new Set<string>();
      for (const key of texSlotKeys) {
        if (overrides[key] != null) graphTexturedSlots.add(key);
      }
      let mapsChanged = false;
      for (const k of texSlotKeys) {
        const incoming = overrides[k];
        const cur = (mat as any)[k] as THREE.Texture | null | undefined;
        if (!cur) continue;
        if (!graphTexturedSlots.has(k)) {
          (mat as any)[k] = null;
          mapsChanged = true;
          continue;
        }
        if (
          incoming instanceof THREE.Texture &&
          (incoming.userData as Record<string, unknown> | undefined)?.__c3dBakePending &&
          !isInFlightBakeOf(incoming, cur)
        ) {
          if (holdPendingMapSlot(mat, k)) mapsChanged = true;
        }
      }

      // Build composite cache key parts — set once at the end so opacity
      // and falloff keys don't overwrite each other.
      const cacheKeyParts: string[] = [];

      // Graph output only — do not blend in global materialOverrides (e.g. from
      // a product "Material" attribute), or unassigned meshes would still pick
      // up that PBR through meshes that share a graph slot.
      {
        const uvMeta = overrides[C3D_UV_RANDOMIZE_UD];
        if (uvMeta && typeof uvMeta === 'object') {
          (mat.userData as any)[C3D_UV_RANDOMIZE_UD] = uvMeta;
        } else {
          delete (mat.userData as any)[C3D_UV_RANDOMIZE_UD];
        }
      }
      if (overrides.color instanceof THREE.Texture) {
        const tex = overrides.color;
        if (textureHasUploadableData(tex)) {
          if (assignTextureIfChanged(mat, 'map', tex)) mapsChanged = true;
        } else {
          if ((mat as any).map) mapsChanged = true;
          deferPendingTexture(pendingTexRef.current, mat, 'map', tex);
        }
        safeColorSet(mat.color, '#ffffff');
      } else if (overrides.color && typeof overrides.color === 'string') {
        safeColorSet(mat.color, overrides.color);
      }
      // Always write core scalars — `if (overrides.x != null)` left GLB-seeded gloss
      // (low roughness / high env) on Wood when the graph relied on defaults.
      mat.roughness = typeof overrides.roughness === 'number' ? overrides.roughness : 0.5;
      mat.metalness = typeof overrides.metalness === 'number' ? overrides.metalness : 0;
      if (overrides.emissive) safeColorSet(mat.emissive, overrides.emissive);
      else safeColorSet(mat.emissive, '#000000');
      mat.emissiveIntensity =
        typeof overrides.emissiveIntensity === 'number' ? overrides.emissiveIntensity : 1;
      applySkipToneMap(mat, {
        skipToneMap:
          overrides.skipToneMap === true || perMeshOverrides?.[child.name]?.skipToneMap === true,
      });
      applyCoatStackFromOverrides(mat, overrides);
      mat.ior = typeof overrides.ior === 'number' ? overrides.ior : 1.5;
      mat.specularIntensity =
        typeof overrides.specularIntensity === 'number' ? overrides.specularIntensity : 1;
      if (overrides.specularColor) safeColorSet(mat.specularColor, overrides.specularColor);
      else safeColorSet(mat.specularColor, '#ffffff');
      mat.envMapIntensity =
        typeof overrides.envMapIntensity === 'number' ? overrides.envMapIntensity : 1;
      mat.iridescence = typeof overrides.iridescence === 'number' ? overrides.iridescence : 0;
      mat.iridescenceMap = null;
      if (overrides.opacity != null) {
        mat.opacity = overrides.opacity;
        if (overrides.opacity < 1 && !((overrides.transmission ?? 0) > 0)) {
          mat.transparent = true;
          mat.depthWrite = false;
          mat.alphaHash = false;
          const opVal = overrides.opacity;

          let opUniform = (mat.userData as any)?._opacityUniform as { value: number } | undefined;
          if (!opUniform) {
            opUniform = { value: opVal };
            (mat.userData as any)._opacityUniform = opUniform;
          }
          opUniform.value = opVal;

          const uOp = opUniform;
          const prevObc = mat.onBeforeCompile;
          mat.onBeforeCompile = (shader, renderer) => {
            if (prevObc) prevObc(shader, renderer);
            shader.uniforms.u_opacityOverride = uOp;
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <common>',
              '#include <common>\nuniform float u_opacityOverride;',
            );
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <dithering_fragment>',
              'gl_FragColor.a *= u_opacityOverride;\n#include <dithering_fragment>',
            );
          };
          cacheKeyParts.push('opacity');
        } else if (overrides.opacity >= 1 && !hasTransmission && !mat.alphaMap) {
          mat.transparent = false;
          mat.depthWrite = true;
        }
      }
      if (hasTransmission) {
        mat.transparent = false;
        mat.depthWrite = true;
        mat.side = THREE.FrontSide;
        // Opacity + transmission = milky / view-dependent glass; keep refraction clean.
        if ((mat.opacity ?? 1) < 0.999) {
          mat.opacity = 1;
        }
        applyTransmitBlurDefine(mat, overrides.transmitBlur ?? 0);
        applyTransmissionAttenuationFromOverrides(mat, overrides, child);
      } else {
        applyTransmitBlurDefine(mat, 0);
      }
      if (overrides.ior != null) mat.ior = overrides.ior;
      // Always write anisotropy — omitted keys inherited Carbon Fiber / metal weave on Wood.
      mat.anisotropy = typeof overrides.anisotropy === 'number' ? overrides.anisotropy : 0;
      if (mat.anisotropy > 0 || overrides.anisotropyMap) {
        ensureAnisotropyTangents(child);
      }
      mat.anisotropyRotation =
        typeof overrides.anisotropyRotation === 'number' ? overrides.anisotropyRotation : 0;
      if (overrides.thickness != null) mat.thickness = overrides.thickness;
      if (overrides.attenuationColor) safeColorSet(mat.attenuationColor, overrides.attenuationColor);
      if (overrides.attenuationDistance != null && overrides.attenuationDistance > 0) {
        mat.attenuationDistance = overrides.attenuationDistance;
      } else if (overrides.attenuationDistance != null && overrides.attenuationDistance <= 0) {
        mat.attenuationDistance = Infinity;
      }
      if (overrides.specularIntensity != null) mat.specularIntensity = overrides.specularIntensity;
      else mat.specularIntensity = 1;
      if (overrides.specularColor) safeColorSet(mat.specularColor, overrides.specularColor);
      else safeColorSet(mat.specularColor, '#ffffff');
      mat.envMapIntensity =
        typeof overrides.envMapIntensity === 'number' ? overrides.envMapIntensity : 1;
      if (overrides.bumpScale != null) mat.bumpScale = overrides.bumpScale;
      if (overrides.normalScale != null) {
        const ns = overrides.normalScale as number;
        mat.normalScale.set(ns, ns);
      }

      // Drop stale deferred slots for this graph material (do not clear global pending — per-mesh meshes may still be loading).
      pendingTexRef.current.delete(mat);

      for (const key of texSlotKeys) {
        const val = overrides[key];
        let tex: THREE.Texture | null = null;
        if (val instanceof THREE.Texture) {
          tex = val;
        } else if (typeof val === 'string' && val) {
          tex = loadTexture(val, LINEAR_TEX_SLOTS.has(key));
        }
        if (tex) {
          if (textureHasUploadableData(tex)) {
            if (!textureHasStableMinFilter(tex)) configureStableTextureSampling(tex);
            if (assignTextureIfChanged(mat, key, tex)) mapsChanged = true;
          } else {
            if ((mat as any)[key]) mapsChanged = true;
            deferPendingTexture(pendingTexRef.current, mat, key, tex);
          }
        }
      }

      syncAnisotropyMapStrength(mat, child);

      applyAlphaMaskFlags(mat, overrides.opacity ?? mat.opacity ?? 1);

      if (overrides._falloff) {
        const { front, side, power } = overrides._falloff;

        // Persistent uniform refs — survive program cache reuse so values
        // update without requiring shader recompilation on each color change.
        let foUniforms = (mat.userData as any)?._falloffUniforms as
          { front: { value: THREE.Color }; side: { value: THREE.Color }; pow: { value: number } } | undefined;
        if (!foUniforms) {
          foUniforms = {
            front: { value: new THREE.Color('#ffffff') },
            side: { value: new THREE.Color('#000000') },
            pow: { value: 2 },
          };
          (mat.userData as any)._falloffUniforms = foUniforms;
        }
        foUniforms.front.value.set(isValidColor(front) ? front : '#ffffff');
        foUniforms.side.value.set(isValidColor(side) ? side : '#000000');
        foUniforms.pow.value = power;

        const uFront = foUniforms.front;
        const uSide = foUniforms.side;
        const uPow = foUniforms.pow;
        const prevObc2 = mat.onBeforeCompile;
        mat.onBeforeCompile = (shader, renderer) => {
          if (prevObc2) prevObc2(shader, renderer);
          shader.uniforms.u_falloffFront = uFront;
          shader.uniforms.u_falloffSide = uSide;
          shader.uniforms.u_falloffPow = uPow;
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <normal_fragment_maps>',
            `#include <normal_fragment_maps>
{
  float falloffNdotV = max(dot(normal, normalize(vViewPosition)), 0.0);
  float falloffFactor = pow(1.0 - falloffNdotV, u_falloffPow);
  vec3 _foBase = mix(u_falloffFront, u_falloffSide, falloffFactor);
#ifdef USE_MAP
  vec4 _foMap = texture2D(map, vMapUv);
  diffuseColor.rgb = _foBase * _foMap.rgb;
#else
  diffuseColor.rgb = _foBase;
#endif
}`,
          );
          shader.fragmentShader =
            'uniform vec3 u_falloffFront;\nuniform vec3 u_falloffSide;\nuniform float u_falloffPow;\n' +
            shader.fragmentShader;
        };
        cacheKeyParts.push('falloff');
      }

      if (overrides._roundedEdge && child instanceof THREE.Mesh && ensureSmoothedNormals(child.geometry)) {
        const reData = overrides._roundedEdge;
        let reUniforms = (mat.userData as any)?._roundedEdgeUniforms as
          { radius: { value: number } } | undefined;
        if (!reUniforms) {
          reUniforms = { radius: { value: 5 } };
          (mat.userData as any)._roundedEdgeUniforms = reUniforms;
        }
        reUniforms.radius.value = reData.radius ?? 5;

        const uRadius = reUniforms.radius;
        const prevObcRe = mat.onBeforeCompile;
        mat.onBeforeCompile = (shader, renderer) => {
          if (prevObcRe) prevObcRe(shader, renderer);
          shader.uniforms.u_roundedEdgeRadius = uRadius;
          shader.vertexShader = 'attribute vec3 smoothNormal;\nvarying vec3 vSmoothNormal;\n' + shader.vertexShader;
          shader.vertexShader = shader.vertexShader.replace(
            '#include <defaultnormal_vertex>',
            '#include <defaultnormal_vertex>\nvSmoothNormal = normalize(normalMatrix * smoothNormal);',
          );
          shader.fragmentShader = 'uniform float u_roundedEdgeRadius;\nvarying vec3 vSmoothNormal;\n' + shader.fragmentShader;
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <normal_fragment_maps>',
            ROUNDED_EDGE_FRAG,
          );
        };
        cacheKeyParts.push('roundedEdge');
      }

      if (overrides._invertAlpha) {
        const prevObcAlpha = mat.onBeforeCompile;
        mat.onBeforeCompile = (shader, renderer) => {
          if (prevObcAlpha) prevObcAlpha(shader, renderer);
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <alphamap_fragment>',
            [
              '#ifdef USE_ALPHAMAP',
              '\tdiffuseColor.a *= ( 1.0 - texture2D( alphaMap, vAlphaMapUv ).g );',
              '#endif',
            ].join('\n'),
          );
        };
        cacheKeyParts.push('invertAlpha');
      }

      if (overrides._materialLayers) {
        const layers = overrides._materialLayers as Array<{
          mask: THREE.Texture;
          maskUvChannel: number;
          color: string;
          map: THREE.Texture | null;
          mapUvChannel?: number;
          normalMap: THREE.Texture | null;
          normalUvChannel?: number;
          bumpMap: THREE.Texture | null;
          bumpUvChannel?: number;
          bumpScale: number;
          roughness: number;
          roughnessMap: THREE.Texture | null;
          roughnessUvChannel?: number;
          metalness: number;
          metalnessMap: THREE.Texture | null;
          metalnessUvChannel?: number;
          amount: number;
        }>;
        const count = Math.min(layers.length, 3);

        type TexSlot = { tex: { value: THREE.Texture | null }; uvMat: { value: THREE.Matrix3 } };
        type LayerUniforms = {
          mask: TexSlot; color: { value: THREE.Color }; map: TexSlot; normalMap: TexSlot;
          bumpMap: TexSlot; bumpScale: { value: number };
          roughness: { value: number }; roughnessMap: TexSlot;
          metalness: { value: number }; metalnessMap: TexSlot;
          amount: { value: number };
        };
        const mkSlot = (): TexSlot => ({ tex: { value: null }, uvMat: { value: new THREE.Matrix3() } });
        let luArr = (mat.userData as any)._layerUniforms as LayerUniforms[] | undefined;
        if (!luArr || luArr.length < count) {
          luArr = [];
          for (let i = 0; i < count; i++) {
            luArr.push({
              mask: mkSlot(), color: { value: new THREE.Color('#ffffff') },
              map: mkSlot(), normalMap: mkSlot(),
              bumpMap: mkSlot(), bumpScale: { value: 1 },
              roughness: { value: 0.5 }, roughnessMap: mkSlot(),
              metalness: { value: 0 }, metalnessMap: mkSlot(),
              amount: { value: 1 },
            });
          }
          (mat.userData as any)._layerUniforms = luArr;
        }

        const syncTex = (slot: TexSlot, tex: THREE.Texture | null) => {
          slot.tex.value = tex;
          if (tex) { tex.updateMatrix(); slot.uvMat.value.copy(tex.matrix); }
          else { slot.uvMat.value.identity(); }
        };

        const flags: Array<{
          hasMap: boolean; hasNormal: boolean; hasBump: boolean; hasRoughMap: boolean; hasMetalMap: boolean;
          maskUvCh: number; mapUvCh: number; normalUvCh: number; bumpUvCh: number; roughUvCh: number; metalUvCh: number;
        }> = [];
        for (let i = 0; i < count; i++) {
          const l = layers[i];
          syncTex(luArr[i].mask, l.mask);
          luArr[i].color.value.set(isValidColor(l.color) ? l.color : '#ffffff');
          syncTex(luArr[i].map, l.map);
          syncTex(luArr[i].normalMap, l.normalMap);
          syncTex(luArr[i].bumpMap, l.bumpMap);
          luArr[i].bumpScale.value = l.bumpScale ?? 1;
          luArr[i].roughness.value = l.roughness;
          syncTex(luArr[i].roughnessMap, l.roughnessMap);
          luArr[i].metalness.value = l.metalness;
          syncTex(luArr[i].metalnessMap, l.metalnessMap);
          luArr[i].amount.value = l.amount;
          flags.push({
            hasMap: !!l.map, hasNormal: !!l.normalMap, hasBump: !!l.bumpMap,
            hasRoughMap: !!l.roughnessMap, hasMetalMap: !!l.metalnessMap,
            maskUvCh: l.maskUvChannel ?? 0,
            mapUvCh: l.mapUvChannel ?? 0,
            normalUvCh: l.normalUvChannel ?? 0,
            bumpUvCh: l.bumpUvChannel ?? 0,
            roughUvCh: l.roughnessUvChannel ?? 0,
            metalUvCh: l.metalnessUvChannel ?? 0,
          });
        }

        const layerUniforms = luArr.slice(0, count);
        const layerFlags = [...flags];

        const prevObcLayers = mat.onBeforeCompile;
        mat.onBeforeCompile = (shader, renderer) => {
          if (prevObcLayers) prevObcLayers(shader, renderer);

          // Build matUvChannels INSIDE the callback so it reflects the
          // current material state (pending textures may have arrived).
          const matUvChannels = new Set<number>();
          for (const prop of ['map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
            'aoMap', 'alphaMap', 'emissiveMap', 'displacementMap', 'clearcoatMap',
            'clearcoatRoughnessMap', 'clearcoatNormalMap', 'anisotropyMap']) {
            const tex = (mat as any)[prop];
            if (tex instanceof THREE.Texture) matUvChannels.add(tex.channel ?? 0);
          }

          let vertVaryings = 'varying vec2 v_layerUv;\n';
          let vertAssigns = 'v_layerUv = uv;\n';
          let fragVaryings = 'varying vec2 v_layerUv;\n';
          const declaredAttrs = new Set<string>();
          const layerVarying = (i: number, slot: 'mask' | 'map' | 'norm' | 'bump' | 'rough' | 'metal') => {
            if (slot === 'mask') return `v_layer${i}_maskUv`;
            if (slot === 'map') return `v_layer${i}_mapUv`;
            if (slot === 'norm') return `v_layer${i}_normUv`;
            if (slot === 'bump') return `v_layer${i}_bumpUv`;
            if (slot === 'rough') return `v_layer${i}_roughUv`;
            return `v_layer${i}_metalUv`;
          };
          type UvNeed = { i: number; slot: 'mask' | 'map' | 'norm' | 'bump' | 'rough' | 'metal'; ch: number };
          const uvNeeds: UvNeed[] = [];
          for (let i = 0; i < count; i++) {
            const f = layerFlags[i];
            if (f.maskUvCh > 0) uvNeeds.push({ i, slot: 'mask', ch: f.maskUvCh });
            if (f.hasMap && f.mapUvCh > 0) uvNeeds.push({ i, slot: 'map', ch: f.mapUvCh });
            if (f.hasNormal && f.normalUvCh > 0) uvNeeds.push({ i, slot: 'norm', ch: f.normalUvCh });
            if (f.hasBump && f.bumpUvCh > 0) uvNeeds.push({ i, slot: 'bump', ch: f.bumpUvCh });
            if (f.hasRoughMap && f.roughUvCh > 0) uvNeeds.push({ i, slot: 'rough', ch: f.roughUvCh });
            if (f.hasMetalMap && f.metalUvCh > 0) uvNeeds.push({ i, slot: 'metal', ch: f.metalUvCh });
          }
          for (const need of uvNeeds) {
            const attr = `uv${need.ch}`;
            const alreadyInShader = shader.vertexShader.includes(`attribute vec2 ${attr}`) || shader.vertexShader.includes(`vec2 ${attr};`);
            if (!declaredAttrs.has(attr) && !matUvChannels.has(need.ch) && !alreadyInShader) {
              vertVaryings += `attribute vec2 ${attr};\n`;
              declaredAttrs.add(attr);
            }
            const vn = layerVarying(need.i, need.slot);
            vertVaryings += `varying vec2 ${vn};\n`;
            vertAssigns += `${vn} = ${attr};\n`;
            fragVaryings += `varying vec2 ${vn};\n`;
          }

          shader.vertexShader = vertVaryings + shader.vertexShader;
          shader.vertexShader = shader.vertexShader.replace(
            '#include <uv_vertex>',
            '#include <uv_vertex>\n' + vertAssigns,
          );

          let uniformDecl = fragVaryings
            + 'vec2 _txUv(mat3 m, vec2 uv) { return (m * vec3(uv, 1.0)).xy; }\n';
          let blendCode = '';
          let normalBlendCode = '';
          for (let i = 0; i < count; i++) {
            const u = layerUniforms[i];
            const f = layerFlags[i];
            shader.uniforms[`u_layer${i}_mask`] = u.mask.tex;
            shader.uniforms[`u_layer${i}_maskMat`] = u.mask.uvMat;
            shader.uniforms[`u_layer${i}_color`] = u.color;
            shader.uniforms[`u_layer${i}_rough`] = u.roughness;
            shader.uniforms[`u_layer${i}_metal`] = u.metalness;
            shader.uniforms[`u_layer${i}_amount`] = u.amount;

            uniformDecl += `uniform sampler2D u_layer${i}_mask;\n`
              + `uniform mat3 u_layer${i}_maskMat;\n`
              + `uniform vec3 u_layer${i}_color;\n`
              + `uniform float u_layer${i}_rough;\n`
              + `uniform float u_layer${i}_metal;\n`
              + `uniform float u_layer${i}_amount;\n`;

            if (f.hasMap) {
              shader.uniforms[`u_layer${i}_map`] = u.map.tex;
              shader.uniforms[`u_layer${i}_mapMat`] = u.map.uvMat;
              uniformDecl += `uniform sampler2D u_layer${i}_map;\nuniform mat3 u_layer${i}_mapMat;\n`;
            }
            if (f.hasNormal) {
              shader.uniforms[`u_layer${i}_normalMap`] = u.normalMap.tex;
              shader.uniforms[`u_layer${i}_normalMapMat`] = u.normalMap.uvMat;
              uniformDecl += `uniform sampler2D u_layer${i}_normalMap;\nuniform mat3 u_layer${i}_normalMapMat;\n`;
            }
            if (f.hasBump) {
              shader.uniforms[`u_layer${i}_bumpMap`] = u.bumpMap.tex;
              shader.uniforms[`u_layer${i}_bumpMapMat`] = u.bumpMap.uvMat;
              shader.uniforms[`u_layer${i}_bumpScale`] = u.bumpScale;
              uniformDecl += `uniform sampler2D u_layer${i}_bumpMap;\nuniform mat3 u_layer${i}_bumpMapMat;\nuniform float u_layer${i}_bumpScale;\n`;
            }
            if (f.hasRoughMap) {
              shader.uniforms[`u_layer${i}_roughMap`] = u.roughnessMap.tex;
              shader.uniforms[`u_layer${i}_roughMapMat`] = u.roughnessMap.uvMat;
              uniformDecl += `uniform sampler2D u_layer${i}_roughMap;\nuniform mat3 u_layer${i}_roughMapMat;\n`;
            }
            if (f.hasMetalMap) {
              shader.uniforms[`u_layer${i}_metalMap`] = u.metalnessMap.tex;
              shader.uniforms[`u_layer${i}_metalMapMat`] = u.metalnessMap.uvMat;
              uniformDecl += `uniform sampler2D u_layer${i}_metalMap;\nuniform mat3 u_layer${i}_metalMapMat;\n`;
            }

            const maskUvVar = f.maskUvCh > 0 ? `v_layer${i}_maskUv` : 'v_layerUv';
            const maskUv = `_txUv(u_layer${i}_maskMat, ${maskUvVar})`;
            const mapUvVar = f.mapUvCh > 0 ? `v_layer${i}_mapUv` : 'v_layerUv';
            const normUvVar = f.normalUvCh > 0 ? `v_layer${i}_normUv` : 'v_layerUv';
            const bumpUvVar = f.bumpUvCh > 0 ? `v_layer${i}_bumpUv` : 'v_layerUv';
            const roughUvVar = f.roughUvCh > 0 ? `v_layer${i}_roughUv` : 'v_layerUv';
            const metalUvVar = f.metalUvCh > 0 ? `v_layer${i}_metalUv` : 'v_layerUv';
            const colorExpr = f.hasMap
              ? `texture2D(u_layer${i}_map, _txUv(u_layer${i}_mapMat, ${mapUvVar})).rgb`
              : `u_layer${i}_color`;
            const roughExpr = f.hasRoughMap
              ? `texture2D(u_layer${i}_roughMap, _txUv(u_layer${i}_roughMapMat, ${roughUvVar})).g`
              : `u_layer${i}_rough`;
            const metalExpr = f.hasMetalMap
              ? `texture2D(u_layer${i}_metalMap, _txUv(u_layer${i}_metalMapMat, ${metalUvVar})).b`
              : `u_layer${i}_metal`;

            blendCode += `{\n`
              + `  float lm${i} = texture2D(u_layer${i}_mask, ${maskUv}).r * u_layer${i}_amount;\n`
              + `  diffuseColor.rgb = mix(diffuseColor.rgb, ${colorExpr}, lm${i});\n`
              + `  roughnessFactor = mix(roughnessFactor, ${roughExpr}, lm${i});\n`
              + `  metalnessFactor = mix(metalnessFactor, ${metalExpr}, lm${i});\n`
              + `}\n`;

            if (f.hasNormal) {
              normalBlendCode += `{\n`
                + `  float lm${i} = texture2D(u_layer${i}_mask, ${maskUv}).r * u_layer${i}_amount;\n`
                + `  vec3 _ln${i} = texture2D(u_layer${i}_normalMap, _txUv(u_layer${i}_normalMapMat, ${normUvVar})).xyz * 2.0 - 1.0;\n`
                + `  normal = normalize(mix(normal, _ln${i}, lm${i}));\n`
                + `}\n`;
            } else if (f.hasBump) {
              normalBlendCode += `{\n`
                + `  float lm${i} = texture2D(u_layer${i}_mask, ${maskUv}).r * u_layer${i}_amount;\n`
                + `  vec2 _bUv${i} = _txUv(u_layer${i}_bumpMapMat, ${bumpUvVar});\n`
                + `  vec2 _bSt0_${i} = dFdx(_bUv${i});\n`
                + `  vec2 _bSt1_${i} = dFdy(_bUv${i});\n`
                + `  float _bHc${i} = texture2D(u_layer${i}_bumpMap, _bUv${i}).r;\n`
                + `  float _bDhx${i} = texture2D(u_layer${i}_bumpMap, _bUv${i} + _bSt0_${i}).r - _bHc${i};\n`
                + `  float _bDhy${i} = texture2D(u_layer${i}_bumpMap, _bUv${i} + _bSt1_${i}).r - _bHc${i};\n`
                + `  vec3 _bQ0_${i} = dFdx(-vViewPosition);\n`
                + `  vec3 _bQ1_${i} = dFdy(-vViewPosition);\n`
                + `  vec3 _bQ1p${i} = cross(_bQ1_${i}, normal);\n`
                + `  vec3 _bQ0p${i} = cross(normal, _bQ0_${i});\n`
                + `  vec3 _bT${i} = _bQ1p${i} * _bSt0_${i}.x + _bQ0p${i} * _bSt1_${i}.x;\n`
                + `  vec3 _bB${i} = _bQ1p${i} * _bSt0_${i}.y + _bQ0p${i} * _bSt1_${i}.y;\n`
                + `  float _bDt${i} = max(dot(_bT${i}, _bT${i}), dot(_bB${i}, _bB${i}));\n`
                + `  float _bSc${i} = (_bDt${i} == 0.0) ? 0.0 : inversesqrt(_bDt${i});\n`
                + `  vec3 _bN${i} = normalize(normal + (_bT${i} * _bDhx${i} + _bB${i} * _bDhy${i}) * _bSc${i} * u_layer${i}_bumpScale);\n`
                + `  normal = normalize(mix(normal, _bN${i}, lm${i}));\n`
                + `}\n`;
            }
            // When layer has neither normalMap nor bumpMap, leave normal unchanged
            // (preserves base material's bump/normal in masked regions)
          }

          shader.fragmentShader = uniformDecl + shader.fragmentShader;
          shader.fragmentShader = shader.fragmentShader.replace(
            '#include <metalnessmap_fragment>',
            '#include <metalnessmap_fragment>\n' + blendCode,
          );
          if (normalBlendCode) {
            shader.fragmentShader = shader.fragmentShader.replace(
              '#include <normal_fragment_maps>',
              '#include <normal_fragment_maps>\n' + normalBlendCode,
            );
          }
        };
        const flagKey = layerFlags.map((f, i) =>
          `${i}${f.hasMap ? 'm' : ''}${f.hasNormal ? 'n' : ''}${f.hasBump ? 'b' : ''}${f.hasRoughMap ? 'r' : ''}${f.hasMetalMap ? 'e' : ''}`
          + `M${f.maskUvCh}P${f.mapUvCh}N${f.normalUvCh}B${f.bumpUvCh}R${f.roughUvCh}E${f.metalUvCh}`
        ).join('_');
        cacheKeyParts.push(`layers${count}_${flagKey}`);
      }

      if (applyGraphEnvOverride(mat, overrides, r3fScene.environment, invalidate)) {
        mapsChanged = true;
      }

      if (cacheKeyParts.length > 0) {
        const composedKey = cacheKeyParts.join('|') + `_${mat.uuid}`;
        mat.customProgramCacheKey = () => composedKey;
      } else {
        mat.customProgramCacheKey = () => '';
      }

      (mat.userData as any).c3dGraphFeaturesKey = featureKey;
      (mat.userData as any).c3dLayerPresenceKey = layerMapPresenceKey(overrides);
      // Only force program rebuild when maps/features actually changed — avoids AO/env flash on tint swipes.
      if (mapsChanged || !reusingGraphMat || (mat.userData as any).c3dLastFeatureKey !== featureKey) {
        mat.needsUpdate = true;
      }
      (mat.userData as any).c3dLastFeatureKey = featureKey;

      // If the alphaMap is still loading, hide the mesh to prevent a solid
      // black flash. The useFrame will reveal it once the texture arrives.
      // Never re-hide when a usable alpha is already bound — option swaps on prod
      // deferred a new alpha while idle SSAA held converged history → ghost trails.
      const pending = pendingTexRef.current.get(mat);
      if (pending && pending.alphaMap) {
        const curAlpha = mat.alphaMap as THREE.Texture | null | undefined;
        const keepVisible =
          curAlpha instanceof THREE.Texture &&
          (textureHasUploadableData(curAlpha) || !!(curAlpha.userData as Record<string, unknown>)?.__c3dBakePending);
        if (!keepVisible) {
          child.visible = false;
          alphaHiddenRef.current.add(child);
          alphaHiddenAtRef.current.set(child, performance.now());
        }
      }

      const meshKey = child.name;
      pendingHoldRef.current.delete(meshKey);
      const pendingSlots = pending ? Object.keys(pending).length : 0;
      if (pendingSlots === 0) {
        const softStamp = softOverrideStamp(shareKey, overrides, featureKey);
        if (softStamp) (mat.userData as any).c3dSoftStamp = softStamp;
      }
      if (pendingSlots > 0) {
        const toSnap = buildTargetSnapshot(mat, pending);
        const toExtras = snapMaterialExtras(mat);
        abortMeshTransition(meshKey, mat);
        // Never restore the previous material while maps bake — that was the Carbon→Wood flash.
        pendingHoldRef.current.set(meshKey, {
          from: snapPBR(mat),
          to: toSnap,
          fromExtras: snapMaterialExtras(mat),
          toExtras,
        });
        invalidate();
      } else {
        const toSnap = snapPBR(mat);
        const toExtras = snapMaterialExtras(mat);
        if (
          fromSnapForTransition &&
          fromSnapForTransition.texSig !== toSnap.texSig
        ) {
          hardSnapMaterial(child.name, mat, toSnap, toExtras);
        } else {
          meshTransition(child.name, mat, fromSnapForTransition, fromExtrasForTransition, toSnap, toExtras);
        }
      }
      ensureTangentsForMappedMaterial(child, mat, overrides);
      child.material = mat;
      syncInstancedMeshMaterialFromSource(child);
      child.userData.c3dGraphMatApplied = true;
    });
    demoteNestedTransmission(clonedScene);
    invalidate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clonedScene, materialGraphOverrides, displayMode, graphApplyGen, preferProgressiveMaterials]);

  // Enforce transparency from materialGraphOverrides (catches any post-effect resets)
  const graphOvRef = useRef(materialGraphOverrides);
  graphOvRef.current = materialGraphOverrides;
  const texLoadedMats = useRef(new WeakSet<THREE.Material>());
  useEffect(() => {
    texLoadedMats.current = new WeakSet();
  }, [materialGraphOverrides]);

  // Demand frameloop: bake / Text Canvas completion dispatches scene-texture-ready.
  // Always invalidate — engraving alphaMap UV clones often finish without a pendingTex
  // entry (blank canvas was already "uploadable"), and still need a GPU re-upload.
  useEffect(() => {
    const kick = () => {
      invalidate();
    };
    document.addEventListener('scene-texture-ready', kick);
    document.addEventListener('scene-graph-textures-idle', kick);
    return () => {
      document.removeEventListener('scene-texture-ready', kick);
      document.removeEventListener('scene-graph-textures-idle', kick);
    };
  }, [invalidate]);

  // Priority < 0: run after OrbitControls (default 0) so envMapRotation uses the settled camera azimuth.
  useFrame((state) => {
    let didWork = false;
    // `frameloop="demand"`: keep scheduling frames while async textures or scalar transitions run so orbit / damping stay smooth.
    if (transRef.current.size > 0) didWork = true;
    if (pendingTexRef.current.size > 0) didWork = true;

    const now = performance.now();
    for (const mesh of Array.from(alphaHiddenRef.current)) {
      const t0 = alphaHiddenAtRef.current.get(mesh);
      if (t0 != null && now - t0 > ALPHA_HIDE_FALLBACK_MS) {
        mesh.visible = true;
        alphaHiddenRef.current.delete(mesh);
        didWork = true;
      }
    }

    // Graph and per-mesh paths both defer async textures; flush whenever data is upload-ready.
    let kickDeferredGraphApply = false;
    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.material || Array.isArray(child.material)) return;
      const boundMat = child.material;
      if (!(boundMat instanceof THREE.MeshPhysicalMaterial)) return;
      const targetMat = (child.name && graphMatsRef.current.get(child.name)) || boundMat;
      const pendingOwner = pendingTexRef.current.has(targetMat) ? targetMat : boundMat;
      const deferred = pendingTexRef.current.get(pendingOwner);
      const mat = pendingOwner;
      if (deferred) {
        let allReady = true;
        for (const tex of Object.values(deferred)) {
          // Image may have decoded since defer — finish Exposure/HSL before the uploadable check.
          promotePendingCpuProcessTexture(tex);
          if (!textureHasUploadableData(tex)) {
            allReady = false;
            break;
          }
        }
        if (allReady) {
          const meshKey = child.name || child.uuid;
          const held = pendingHoldRef.current.get(meshKey);
          for (const tex of Object.values(deferred)) {
            if (!textureHasStableMinFilter(tex)) configureStableTextureSampling(tex);
          }
          pendingTexRef.current.delete(mat);

          if (held?.keepLiveUntilReady) {
            // Live mesh still shows the previous finish. Re-run graph apply now that maps are ready.
            pendingHoldRef.current.delete(meshKey);
            kickDeferredGraphApply = true;
            didWork = true;
            return;
          }

          if (held) {
            // Atomic snap to the complete target (maps + scalars). Assigning maps first then
            // meshTransition left new UVs on screen while old metalness/coat lerped — washed
            // "white wood" that still looked like the previous material.
            pendingHoldRef.current.delete(meshKey);
            hardSnapMaterial(meshKey, boundMat, held.to, held.toExtras);
          } else {
            for (const [slot, tex] of Object.entries(deferred)) {
              if (slot.includes(':')) continue;
              (boundMat as any)[slot] = tex;
            }
          }

          // Deferred normal/aniso maps often arrive after the first apply — regenerate
          // tangents now or Metal stays flat until the next Yellow↔Silver switch.
          ensureTangentsForMappedMaterial(child, boundMat);
          if (boundMat.anisotropyMap && child instanceof THREE.Mesh) {
            syncAnisotropyMapStrength(boundMat, child);
          }
          applyAlphaMaskFlags(boundMat, boundMat.opacity ?? 1);
          // Maps are live — stamp so Cap/Body sharing can skip redundant re-applies.
          {
            const ovNow = graphOvRef.current?.[child.name] ?? graphOvRef.current?.['*'];
            if (ovNow) {
              const fk = graphFeatureCacheKey(ovNow);
              const sk =
                typeof (ovNow as { __c3dShareKey?: unknown }).__c3dShareKey === 'string'
                  ? (ovNow as { __c3dShareKey: string }).__c3dShareKey
                  : null;
              const stamp = softOverrideStamp(sk, ovNow, fk);
              if (stamp) (boundMat.userData as any).c3dSoftStamp = stamp;
            }
          }
          boundMat.needsUpdate = true;
          if (alphaHiddenRef.current.has(child)) {
            child.visible = true;
            alphaHiddenRef.current.delete(child);
          }
          // Demand frameloop + idle AA: notify once maps settle (debounced globally).
          if (typeof document !== 'undefined') {
            dispatchMaterialMapsReadyDebounced();
          }
          didWork = true;
        } else {
          didWork = true;
        }
      }
    });

    if (kickDeferredGraphApply) {
      for (const [k, h] of pendingHoldRef.current) {
        if (h.keepLiveUntilReady) pendingHoldRef.current.delete(k);
      }
      graphApplyGenRef.current += 1;
      const next = graphApplyGenRef.current;
      queueMicrotask(() => setGraphApplyGen(next));
    }

    const ov = graphOvRef.current;
    if (!ov) {
      if (didWork) state.invalidate();
      return;
    }
    const activeTransitions = transRef.current;
    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.name) return;
      const overrides = ov[child.name] ?? ov['*'];
      if (!overrides) return;
      const mat = child.material;
      if (!mat || Array.isArray(mat)) return;
      if (!(mat instanceof THREE.MeshPhysicalMaterial)) return;

      const meshKey = child.name || child.uuid;
      if (pendingHoldRef.current.get(meshKey)?.keepLiveUntilReady) return;
      const isTransitioning = activeTransitions.has(meshKey);

      // Enforce transmission (skip during active transition to avoid ping-pong).
      // Nested inner volumes (e.g. liquid in glass) are demoted to opaque — do not re-apply.
      if (
        !isTransitioning &&
        !child.userData.c3dNestedTransmissionDemoted &&
        overrides.transmission != null &&
        overrides.transmission > 0
      ) {
        if (mat.transmission !== overrides.transmission || mat.transparent !== false || (mat.opacity ?? 1) < 0.999) {
          mat.transmission = overrides.transmission;
          mat.transparent = false;
          mat.depthWrite = true;
          mat.side = THREE.FrontSide;
          mat.opacity = 1;
          if (overrides.ior != null) mat.ior = overrides.ior;
          if (overrides.thickness != null) mat.thickness = overrides.thickness;
          applyTransmissionAttenuationFromOverrides(mat, overrides, child);
          mat.needsUpdate = true;
        }
        applyTransmitBlurDefine(mat, overrides.transmitBlur ?? 0);
      }
      // Enforce opacity (skip during active transition) — not for demoted nested solids
      if (
        !isTransitioning &&
        !child.userData.c3dNestedTransmissionDemoted &&
        overrides.opacity != null &&
        overrides.opacity < 1
      ) {
        if (!mat.transparent || mat.opacity !== overrides.opacity) {
          mat.opacity = overrides.opacity;
          mat.transparent = true;
          mat.depthWrite = false;
        }
      }
      // Enforce alphaMap transparency (only when image is loaded)
      if (alphaMapReady(mat)) {
        applyAlphaMaskFlags(mat, overrides.opacity ?? mat.opacity ?? 1);
        if (!mat.transparent) mat.needsUpdate = true;
      }

      // Poll already-assigned textures whose images may still be loading
      if (!texLoadedMats.current.has(mat) && !pendingTexRef.current.has(mat)) {
        const texSlots = ['map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
          'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
          'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap'];
        let pending = false;
        for (const slot of texSlots) {
          const tex = (mat as any)[slot] as THREE.Texture | null;
          if (tex && !textureHasUploadableData(tex)) { pending = true; break; }
        }
        if (!pending) {
          texLoadedMats.current.add(mat);
          applyAlphaMaskFlags(mat, mat.opacity ?? 1);
          if (mat.anisotropyMap && child instanceof THREE.Mesh) {
            syncAnisotropyMapStrength(mat, child);
          }
          mat.needsUpdate = true;
          if (child instanceof THREE.Mesh && alphaHiddenRef.current.has(child)) {
            child.visible = true;
            alphaHiddenRef.current.delete(child);
          }
          didWork = true;
        } else {
          didWork = true;
        }
      }
      // Per-material env rotation (and optional camera link). Do not require mat.envMap: HDR may
      // still be loading async; base rotation alone was wrong until first interaction without this.
      if (overrides._envOverride && (mat as any).envMapRotation) {
        const eo = overrides._envOverride as { rotation?: number; linkCamera?: boolean };
        const baseRad = ((eo.rotation ?? 0) * Math.PI) / 180;
        let targetY = baseRad;
        if (eo.linkCamera) {
          targetY += cameraLinkedEnvYaw(state.camera, getOrbitTarget(state.controls));
        }
        const cur = (mat as any).envMapRotation as THREE.Euler;
        if (Math.abs(cur.y - targetY) > 1e-5) {
          cur.set(0, targetY, 0);
          didWork = true;
        }
      }
    });

    // First-paint gate: wait until graph mats are applied and deferred textures can upload.
    if (!contentReadyRef.current && loadedRef.current) {
      let ready = true;
      if (holdUntilReady && !materialsGateReady) {
        ready = false;
      }
      const ovMap = graphOvRef.current;
      if (ready && ovMap && Object.keys(ovMap).length > 0) {
        let expectsGraph = false;
        clonedScene.traverse((child) => {
          if (!(child instanceof THREE.Mesh)) return;
          const slot = ovMap[child.name || ''] ?? ovMap['*'];
          if (!slot) return;
          expectsGraph = true;
          if (!child.userData.c3dGraphMatApplied) ready = false;
        });
        if (expectsGraph && !ready) {
          /* still applying graphs */
        }
      }
      if (ready && pendingTexRef.current.size > 0) {
        ready = false;
      }
      if (ready) {
        clonedScene.traverse((child) => {
          if (!ready) return;
          if (!(child instanceof THREE.Mesh) || !child.material || Array.isArray(child.material)) return;
          const mat = child.material as THREE.MeshPhysicalMaterial;
          for (const key of GRAPH_TEX_SLOT_KEYS) {
            if (isBakePlaceholder((mat as any)[key])) {
              ready = false;
              return;
            }
          }
        });
      }
      if (ready && alphaHiddenRef.current.size > 0) {
        ready = false;
      }
      // Per-model deferred maps (including Desaturate/HSL CPU bakes) must land before
      // first paint — revealing with a missing albedo/roughness map made Carpaint flash
      // darker and Wood flash glossy, then snap back when the bake finished.
      if (ready) {
        clonedScene.traverse((child) => {
          if (!ready) return;
          if (!(child instanceof THREE.Mesh) || !child.material || Array.isArray(child.material)) return;
          const mat = child.material as THREE.MeshPhysicalMaterial;
          for (const slot of [
            'map', 'normalMap', 'bumpMap', 'roughnessMap', 'metalnessMap',
            'emissiveMap', 'aoMap', 'alphaMap', 'displacementMap', 'anisotropyMap',
            'clearcoatMap', 'clearcoatRoughnessMap', 'clearcoatNormalMap',
          ] as const) {
            const tex = (mat as any)[slot] as THREE.Texture | null;
            if (!tex) continue;
            if ((tex.userData as Record<string, unknown> | undefined)?.__c3dBakePending) continue;
            if (!textureHasUploadableData(tex)) {
              ready = false;
              break;
            }
          }
        });
      }
      if (ready) {
        markContentReady();
        didWork = true;
      }
    }

    if (didWork) state.invalidate();
  }, -1);

  useEffect(() => {
    if (!holdUntilReady) return;
    if (contentReadyRef.current) return;
    if (!materialsGateReady) return;
    // Safety only for stuck bookkeeping (404 / never-finishing bake). Normal path
    // waits until this model's deferred maps — including CPU Desaturate/HSL — flush.
    const startedAt = performance.now();
    const HARD_CAP_MS = 4000;
    const t = window.setInterval(() => {
      if (contentReadyRef.current) {
        window.clearInterval(t);
        return;
      }
      const elapsed = performance.now() - startedAt;
      let placeholders = false;
      if (pendingTexRef.current.size === 0) {
        clonedScene.traverse((child) => {
          if (placeholders) return;
          if (!(child instanceof THREE.Mesh) || !child.material || Array.isArray(child.material)) return;
          const mat = child.material as THREE.MeshPhysicalMaterial;
          for (const key of GRAPH_TEX_SLOT_KEYS) {
            if (isBakePlaceholder((mat as any)[key])) {
              placeholders = true;
              return;
            }
          }
        });
      }
      if ((pendingTexRef.current.size === 0 && !placeholders) || elapsed >= HARD_CAP_MS) {
        window.clearInterval(t);
        markContentReady();
      }
    }, 200);
    return () => window.clearInterval(t);
  }, [holdUntilReady, materialsGateReady, clonedScene, markContentReady]);

  // Shadows
  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = castShadow;
        child.receiveShadow = receiveShadow;
      }
    });
    invalidate();
  }, [clonedScene, castShadow, receiveShadow]);

  // SSAO exclusion via N8AO userData flag
  useEffect(() => {
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.userData.treatAsOpaque = !excludeFromSSAO;
      }
    });
    invalidate();
  }, [clonedScene, excludeFromSSAO]);

  // Omit surface-mounted trim from the glass transmission / refraction buffer
  useEffect(() => {
    clonedScene.traverse((child) => {
      child.userData.excludeFromRefraction = excludeFromRefraction;
      if (child instanceof THREE.Mesh) {
        child.userData.excludeFromRefraction = excludeFromRefraction;
      }
    });
    // Propagate to Array Radial InstancedMeshes (siblings under ModifiedMesh).
    let p: THREE.Object3D | null = clonedScene.parent;
    while (p) {
      const getter = (p.userData as { getC3dInstancedMeshes?: () => THREE.InstancedMesh[] }).getC3dInstancedMeshes;
      if (typeof getter === 'function') {
        for (const im of getter()) {
          im.userData.excludeFromRefraction = excludeFromRefraction;
        }
        break;
      }
      p = p.parent;
    }
    invalidate();
  }, [clonedScene, excludeFromRefraction]);

  // Depth offset (polygon offset) to fix z-fighting on overlay/engraving geometry
  useEffect(() => {
    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.material) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const m of mats) {
        if (!m) continue;
        if (depthOffset !== 0) {
          m.polygonOffset = true;
          m.polygonOffsetFactor = -depthOffset;
          m.polygonOffsetUnits = -depthOffset;
        } else {
          m.polygonOffset = false;
          m.polygonOffsetFactor = 0;
          m.polygonOffsetUnits = 0;
        }
      }
    });
    invalidate();
  }, [clonedScene, depthOffset]);

  // Optional z-priority for objects that should draw after splats while preserving normal self-depth.
  useEffect(() => {
    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.material) return;
      child.renderOrder = stayOnTop ? 20000 : 0;
      child.onBeforeRender = () => {};
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      for (const m of mats) {
        if (!m) continue;
        m.depthTest = true;
        m.needsUpdate = true;
      }
    });
    invalidate();
  }, [clonedScene, stayOnTop]);

  // Wireframe overlay (LineSegments on top of shaded geometry)
  const wireOverlayRef = useRef<THREE.LineSegments[]>([]);
  useEffect(() => {
    const existing = wireOverlayRef.current;
    for (const ls of existing) {
      ls.parent?.remove(ls);
      ls.geometry.dispose();
      (ls.material as THREE.Material).dispose();
    }
    existing.length = 0;

    if (!wireframe) return;

    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh) || !child.geometry) return;
      const wireGeo = new THREE.WireframeGeometry(child.geometry);
      const wireMat = new THREE.LineBasicMaterial({
        color: 0x000000,
        opacity: 0.15,
        transparent: true,
        depthTest: true,
      });
      const lines = new THREE.LineSegments(wireGeo, wireMat);
      lines.raycast = () => {};
      child.add(lines);
      existing.push(lines);
    });

    return () => {
      for (const ls of existing) {
        ls.parent?.remove(ls);
        ls.geometry.dispose();
        (ls.material as THREE.Material).dispose();
      }
      existing.length = 0;
    };
  }, [clonedScene, wireframe]);

  // Display mode override (wireframe, matte, glossy, normals, UV checker, graphOverride)
  const savedMatsRef = useRef<Map<THREE.Mesh, THREE.Material | THREE.Material[] | null>>(new Map());
  const graphOverrideMatRef = useRef<THREE.MeshPhysicalMaterial | null>(null);
  useEffect(() => {
    const saved = savedMatsRef.current;

    saved.forEach((mat, mesh) => {
      if (mat != null) mesh.material = mat;
    });
    saved.clear();

    if (displayMode === 'default') return;

    // graphOverride: build ONE shared material from the wildcard graph overrides
    if (displayMode === 'graphOverride') {
      const wildcard = materialGraphOverrides?.['*'];
      if (!wildcard || Object.keys(wildcard).length === 0) return;

      if (!graphOverrideMatRef.current) {
        graphOverrideMatRef.current = new THREE.MeshPhysicalMaterial();
      }
      const mat = graphOverrideMatRef.current;
      mat.color.set('#ffffff');
      mat.roughness = 1;
      mat.metalness = 0;
      mat.map = null;
      mat.normalMap = null;
      mat.bumpMap = null;
      mat.roughnessMap = null;
      mat.metalnessMap = null;
      mat.emissiveMap = null;
      mat.aoMap = null;
      mat.alphaMap = null;

      // Graph PBR puts albedo on `map` with `color` as a multiply tint string — not `color` as Texture.
      if (wildcard.color && typeof wildcard.color === 'string') {
        safeColorSet(mat.color, wildcard.color);
      }
      if (wildcard.roughness != null) mat.roughness = wildcard.roughness;
      if (wildcard.metalness != null) mat.metalness = wildcard.metalness;
      if (wildcard.emissive) safeColorSet(mat.emissive, wildcard.emissive);
      if (wildcard.emissiveIntensity != null) mat.emissiveIntensity = wildcard.emissiveIntensity;

      const texSlotKeys: Array<[string, boolean]> = [
        ['map', false],
        ['normalMap', true], ['bumpMap', true], ['roughnessMap', true],
        ['metalnessMap', true], ['emissiveMap', false], ['aoMap', true],
        ['alphaMap', true],
      ];
      for (const [key, linear] of texSlotKeys) {
        const val = wildcard[key];
        let tex: THREE.Texture | null = null;
        if (val instanceof THREE.Texture) {
          tex = val;
        } else if (typeof val === 'string' && val) {
          tex = loadTexture(val, linear);
        }
        if (tex) {
          if (textureHasUploadableData(tex)) {
            (mat as any)[key] = tex;
          } else {
            deferPendingTexture(pendingTexRef.current, mat, key, tex);
          }
        }
      }

      if (!mat.map && wildcard.color instanceof THREE.Texture) {
        if (textureHasUploadableData(wildcard.color)) mat.map = wildcard.color;
        else {
          deferPendingTexture(pendingTexRef.current, mat, 'map', wildcard.color);
        }
        mat.color.set('#ffffff');
      }

      if (wildcard.bumpScale != null) mat.bumpScale = wildcard.bumpScale;
      if (wildcard.normalScale != null) {
        const ns = wildcard.normalScale as number;
        mat.normalScale.set(ns, ns);
      }

      mat.needsUpdate = true;

      clonedScene.traverse((child) => {
        if (!(child instanceof THREE.Mesh)) return;
        saved.set(
          child,
          child.material ? (Array.isArray(child.material) ? [...child.material] : child.material) : null,
        );
        child.material = mat;
      });

      return () => {
        saved.forEach((m, mesh) => {
          if (m != null) mesh.material = m;
        });
        saved.clear();
      };
    }

    clonedScene.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) return;
      saved.set(
        child,
        child.material ? (Array.isArray(child.material) ? [...child.material] : child.material) : null,
      );

      switch (displayMode) {
        case 'matteWhite':
          child.material = new THREE.MeshStandardMaterial({
            color: 0xffffff, roughness: 1, metalness: 0,
          });
          break;
        case 'glossyGray':
          child.material = new THREE.MeshPhysicalMaterial({
            color: 0x999999, roughness: 0.08, metalness: 0.6, clearcoat: 0.4, clearcoatRoughness: 0.05,
          });
          break;
        case 'normals':
          child.material = new THREE.MeshNormalMaterial();
          break;
        case 'uvChecker':
          child.material = new THREE.MeshStandardMaterial({
            map: getUvCheckerTexture(), roughness: 1, metalness: 0,
          });
          break;
      }
    });

    return () => {
      saved.forEach((mat, mesh) => {
        if (mat != null) mesh.material = mat;
      });
      saved.clear();
    };
  }, [clonedScene, displayMode, materialGraphOverrides, perMeshOverrides]);

  // Per-mesh visibility
  useEffect(() => {
    if (!meshVisibility) return;
    clonedScene.traverse((child) => {
      if (child instanceof THREE.Mesh && child.name) {
        child.visible = meshVisibility[child.name] !== false;
      }
    });
    invalidate();
  }, [clonedScene, meshVisibility]);

  // Opacity crossfade: when opacityOverride changes, start a fade
  useEffect(() => {
    if (opacityOverride == null) return;
    const duration = opacityDurationRef.current;
    if (duration <= 0 || !mountedRef.current) {
      fadeCurrentRef.current = opacityOverride;
      fadeTargetRef.current = opacityOverride;
      fadeProgressRef.current = 1;
      clonedScene.traverse((child) => {
        if (child instanceof THREE.Mesh && child.material && !Array.isArray(child.material)) {
          const mat = child.material as THREE.MeshPhysicalMaterial;
          const gOv = graphOvRef.current;
          const meshOv = gOv?.[child.name] ?? gOv?.['*'];
          const graphOpacity = meshOv?.opacity ?? 1;
          const finalOpacity = graphOpacity * opacityOverride;
          mat.opacity = finalOpacity;
          const hasAlpha = alphaMapReady(mat);
          const needsAlpha = finalOpacity < 1 || hasAlpha;
          mat.transparent = needsAlpha;
          if (finalOpacity < 1 && !hasAlpha) {
            mat.depthWrite = false;
          }
        }
      });
      if (opacityOverride <= 0) fadeCallbackRef.current?.();
      return;
    }
    // If visibility flips again mid-fade, snap "from" to the opacity we're actually drawing now.
    // Otherwise fadeCurrentRef still holds the *start* of the old segment → one-frame jump (~50%) then settle.
    const p = fadeProgressRef.current;
    const prevTarget = fadeTargetRef.current;
    if (p < 1 && prevTarget != null) {
      const te = 1 - Math.pow(1 - p, 3);
      const from = fadeCurrentRef.current;
      fadeCurrentRef.current = from + (prevTarget - from) * te;
    }
    fadeTargetRef.current = opacityOverride;
    fadeProgressRef.current = 0;
  }, [opacityOverride, clonedScene]);

  // Mark mounted AFTER initial effects — first render snaps, subsequent renders transition
  useEffect(() => { mountedRef.current = true; }, []);

  // Scalar material transitions (same texture set only) + product opacity fade.
  // Do not traverse the whole scene during mesh transitions — that rewrote transparent/depthWrite on
  // every mesh and caused metallic / sorting artifacts. Opacity pass runs only for rule fade.
  useFrame((state, rawDelta) => {
    const duration = durationRef.current;
    const opacityDuration = opacityDurationRef.current;
    const delta = Math.min(rawDelta, 1 / 30);
    let fadeTick = false;

    if (fadeTargetRef.current != null && fadeProgressRef.current < 1 && opacityDuration > 0) {
      fadeProgressRef.current = Math.min(fadeProgressRef.current + delta / opacityDuration, 1);
      if (fadeProgressRef.current >= 1) {
        fadeCurrentRef.current = fadeTargetRef.current;
        if (fadeTargetRef.current <= 0) fadeCallbackRef.current?.();
      }
      fadeTick = true;
    }

    const ruleFadeOpacity = (() => {
      if (fadeTargetRef.current == null) return 1;
      if (opacityDuration <= 0) return fadeTargetRef.current;
      if (fadeProgressRef.current >= 1) return fadeTargetRef.current;
      const te = 1 - Math.pow(1 - fadeProgressRef.current, 3);
      return fadeCurrentRef.current + (fadeTargetRef.current - fadeCurrentRef.current) * te;
    })();

    const fadeActive =
      fadeTargetRef.current != null && fadeProgressRef.current < 1 && opacityDuration > 0;

    if (duration > 0 && transRef.current.size > 0) {
      const clockActive = isMaterialTransitionClockActive();
      const { raw, t } = materialTransitionBlend();
      const done: string[] = [];
      for (const [key, tr] of transRef.current) {
        // Editor / hosts that never arm the shared clock: finish immediately at `to`.
        if (!clockActive) {
          applySnap(tr.mat, tr.to, true);
          applyMaterialExtras(tr.mat, tr.toExtras);
          (tr.mat.userData as any).__c3dMatTransitioning = false;
          const pendingStamp = (tr.mat.userData as any).c3dSoftStampPending as string | undefined;
          if (pendingStamp) {
            (tr.mat.userData as any).c3dSoftStamp = pendingStamp;
            delete (tr.mat.userData as any).c3dSoftStampPending;
          }
          tr.mat.needsUpdate = true;
          done.push(key);
          continue;
        }
        // Shared module clock — Cap (shell) and Body (array) sit on the same blend factor.
        lerpSnap(tr.mat, tr.from, tr.to, t);
        lerpMaterialExtras(tr.mat, tr.fromExtras, tr.toExtras, t);
        if (raw >= 1) {
          applySnap(tr.mat, tr.to, true);
          applyMaterialExtras(tr.mat, tr.toExtras);
          (tr.mat.userData as any).__c3dMatTransitioning = false;
          const pendingStamp = (tr.mat.userData as any).c3dSoftStampPending as string | undefined;
          if (pendingStamp) {
            (tr.mat.userData as any).c3dSoftStamp = pendingStamp;
            delete (tr.mat.userData as any).c3dSoftStampPending;
          }
          tr.mat.needsUpdate = true;
          done.push(key);
        }
      }
      for (const key of done) transRef.current.delete(key);
      state.invalidate();
    }

    // One more pass when fade hits 100% (fadeTick) so opacity snaps to target; not during mesh-only transitions.
    const needOpacityPass = fadeActive || fadeTick;

    if (needOpacityPass) {
      const gOv = graphOvRef.current;
      clonedScene.traverse((child) => {
        if (!(child instanceof THREE.Mesh) || !child.material || Array.isArray(child.material)) return;
        const mat = child.material as THREE.MeshPhysicalMaterial;
        const meshOv = gOv?.[child.name] ?? gOv?.['*'];
        const graphOpacity = meshOv?.opacity ?? 1;
        const finalOpacity = graphOpacity * ruleFadeOpacity;
        mat.opacity = finalOpacity;
        const hasAlpha = alphaMapReady(mat);
        const needsAlpha = finalOpacity < 1 || hasAlpha;
        mat.transparent = needsAlpha;
        mat.depthWrite = !needsAlpha || hasAlpha;
      });
      state.invalidate();
    }
  });

  return (
    <group
      ref={groupRef}
      position={safePosition}
      rotation={safeRotation}
      scale={safeScale}
      visible={visible !== false && (!holdUntilReady || contentReady)}
      onClick={onClick}
      onPointerOver={onPointerOver}
      onPointerOut={onPointerOut}
    >
      <primitive object={clonedScene} />
    </group>
  );
});

const GlbModelGltfInner = forwardRef<GlbModelHandle, GlbModelProps>(function GlbModelGltfInner(props, ref) {
  const { scene, animations } = useGLTF(props.url, true, true, extendGltfLoader as any);
  return <GlbModelLoadedCore {...props} ref={ref} sourceScene={scene} sourceAnimations={animations} />;
});

const GlbModelFbxInner = forwardRef<GlbModelHandle, GlbModelProps>(function GlbModelFbxInner(props, ref) {
  const fbx = useLoader(FBXLoader, props.url) as THREE.Group;
  const animations = fbx.animations ?? [];
  return <GlbModelLoadedCore {...props} ref={ref} sourceScene={fbx} sourceAnimations={animations} />;
});

const GlbModelInnerRef = forwardRef<GlbModelHandle, GlbModelProps>(function GlbModelInnerRouter(props, ref) {
  if (isFbxAssetUrl(props.url)) {
    return <GlbModelFbxInner {...props} ref={ref} />;
  }
  return <GlbModelGltfInner {...props} ref={ref} />;
});

class GlbModelErrorBoundary extends React.Component<
  { children: React.ReactNode; onError?: () => void; resetKey?: string },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode; onError?: () => void; resetKey?: string }) {
    super(props);
    this.state = { hasError: false };
  }
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  componentDidUpdate(prevProps: { resetKey?: string }) {
    if (this.state.hasError && prevProps.resetKey !== this.props.resetKey) {
      this.setState({ hasError: false });
    }
  }
  componentDidCatch(error: Error) {
    console.warn('[GlbModel] Failed to load model:', error.message);
    this.props.onError?.();
  }
  render() {
    if (this.state.hasError) return null;
    return this.props.children;
  }
}

export const GlbModel = forwardRef<GlbModelHandle, GlbModelProps>(function GlbModel(props, ref) {
  if (!props.url) {
    return null;
  }
  const share =
    props.materialGraphOverrides?.['*']?.__c3dShareKey ??
    Object.values(props.materialGraphOverrides ?? {})[0]?.__c3dShareKey ??
    '';
  const resetKey = `${props.url}|${String(share)}`;
  return (
    <GlbModelErrorBoundary onError={props.onLoaded} resetKey={resetKey}>
      <GlbModelInnerRef {...props} ref={ref} />
    </GlbModelErrorBoundary>
  );
});
